// ============================================================
// VCS.ChatMessages — Nachrichten-Engine
// - Nachrichten laden (Paginierung), rendern (Chat-Bubbles)
// - Text/Bild/Video/Voice/Voicephoto senden
// - Nachrichten bearbeiten, loeschen, weiterleiten
// - Uebersetzungs-Platzhalter + message-translated Events
// - Antworten (Reply), Reaktionen, Lesebestaetigung
// - Scroll-Verhalten, ungelesene Trennlinie
// ============================================================
window.VCS = window.VCS || {};

VCS.ChatMessages = class {

    // ── Konstruktor ──────────────────────────────────────────

    constructor() {
        this._emojiPickerReady = false;
        this._emojiCursorPos = 0;
        this._emojiKeepFocus = false;
        // Mobil: Picker weicht der Tastatur (siehe _closeEmojiForKeyboard).
        this._emojiClosing = false;
        this._emojiCloseTimer = null;
        this._emojiFallbackTimer = null;
        this._emojiVvHandler = null;
        // iOS: App-Hoehe folgt bei offener Tastatur dem sichtbaren Bereich.
        this._iosFitHandler = null;
        this._fakeCaretEl = null;
        // Desktop-Emoji (Telegram-Stil): Hover -> schwebendes Popup, Klick -> rechte
        // Seitenleiste. _emojiSidebarOpen merkt den Zustand, _emojiHoverTimer
        // ueberbrueckt die Luecke Button<->Popup beim Hovern.
        this._emojiSidebarOpen = false;
        this._emojiHoverTimer = null;
        this._view = {};
        // Nachrichten-Pagination (Fenster-Laden wie WhatsApp/Telegram):
        // initial nur die neuesten `_pageSize`, aeltere beim Hochscrollen nachladen.
        this._pageSize = 20;
        this._hasMoreOlder = {};   // chatId -> gibt es noch aeltere Nachrichten am Server?
        this._loadingOlder = {};   // chatId -> laeuft gerade ein Nachlade-Request?
        // Sticker-Auftritts-Effekt (Pop + Partikel) NUR bei neu hinzugefuegten
        // Nachrichten, nicht beim Rendern der Historie. Hier landen die IDs der
        // Nachrichten, die beim naechsten Render einmalig poppen sollen; der
        // Render konsumiert die Marke wieder.
        this._stickerPopPending = new Set();
        // Zustell-Acks koennen eintreffen, bevor die Nachricht mit ihrer echten ID
        // im State liegt (bis dahin haengt dort die temp-ID des Sendevorgangs).
        // Solche Acks landen hier und werden beim Einfuegen nachgezogen, statt
        // verloren zu gehen — sonst bleibt die Ampel bis zum Neuladen stehen.
        this._pendingStatus = {};
        // Ein delegierter animationstart-Listener startet den Emoji-Burst,
        // sobald die Pop-Animation eines Stickers beginnt.
        this._ensureStickerFxListener();
    }

    // ── Öffentliche Methoden ─────────────────────────────────

    async loadMessages(chatId) {
        try {
            const res = await fetch(VCS.Chat.API_BASE + '/messages/' + encodeURIComponent(chatId) + '?limit=' + this._pageSize, {
                headers: VCS.chat?.authHeaders()
            });
            if (!res.ok) {
                return;
            }
            const loaded = await res.json();
            // Volle Seite => es gibt vermutlich noch aeltere Nachrichten zum Nachladen
            this._hasMoreOlder[chatId] = loaded.length >= this._pageSize;
            this._loadingOlder[chatId] = false;
            this._postProcessLoaded(loaded);
            VCS.state.chatMessages[chatId] = loaded;
            this.renderChatMessages();
            this.scrollToBottom();
            this.markAsRead(chatId);
            VCS.e2e?.decryptAllMessages?.(chatId).catch(() => {});
        } catch (e) {
            console.error('[chat] loadMessages error:', e);
        }
    }

    // Server-Rohdaten fuer die Anzeige aufbereiten (Delivered-/Read-Zeit,
    // Voicephoto-/Video-Woerter fuer Karaoke). Wird von Initial-Load und
    // Nachladen gemeinsam genutzt.
    _postProcessLoaded(arr) {
        for (const m of arr) {
            if (m.deliveredAt) m._deliveredAt = m.deliveredAt;
            if (m.readAt) m._readAt = m.readAt;
            m._translationDone = true;
            if (m.mediaType === 'voicephoto' && m.transcription) {
                try {
                    const parsed = JSON.parse(m.transcription);
                    if (Array.isArray(parsed)) {
                        m._vpWords = parsed;
                    } else {
                        m._vpWords = parsed.original || [];
                        if (parsed.translated) { m._vpWordsTranslated = parsed.translated; m._vpTargetLang = parsed.targetLang; }
                    }
                } catch (_) {}
            }
            if (m.mediaType === 'video' && m.transcription) {
                try {
                    const parsed = JSON.parse(m.transcription);
                    m._videoWords = parsed.original || [];
                    if (parsed.translated) { m._videoWordsTranslated = parsed.translated; m._videoTargetLang = parsed.targetLang; }
                } catch (_) {}
            }
        }
    }

    // Aeltere Nachrichten nachladen (beim Hochscrollen). Holt die naechste Seite
    // vor der aeltesten geladenen Nachricht, haengt sie vorne an und haelt dabei
    // die Scroll-Position stabil (sonst springt die Ansicht).
    async loadOlderMessages(chatId) {
        if (this._loadingOlder[chatId] || !this._hasMoreOlder[chatId]) return;
        const msgs = VCS.state.chatMessages[chatId] || [];
        if (msgs.length === 0) return;
        const oldest = msgs[0].timestamp;
        this._loadingOlder[chatId] = true;
        try {
            const url = VCS.Chat.API_BASE + '/messages/' + encodeURIComponent(chatId)
                + '?before=' + oldest + '&limit=' + this._pageSize;
            const res = await fetch(url, { headers: VCS.chat?.authHeaders() });
            if (!res.ok) return;
            const older = await res.json();
            if (older.length < this._pageSize) this._hasMoreOlder[chatId] = false;
            if (older.length === 0) return;
            this._postProcessLoaded(older);
            VCS.state.chatMessages[chatId] = older.concat(VCS.state.chatMessages[chatId] || []);
            // Nur neu rendern, wenn der Chat noch offen ist; Scroll-Position erhalten
            if (VCS.state.chatActiveChatId === chatId) {
                const container = $('#chatMessages')[0];
                const prevHeight = container ? container.scrollHeight : 0;
                const prevTop = container ? container.scrollTop : 0;
                this.renderChatMessages();
                if (container) container.scrollTop = container.scrollHeight - prevHeight + prevTop;
            }
            VCS.e2e?.decryptAllMessages?.(chatId).catch(() => {});
        } catch (e) {
            console.error('[chat] loadOlderMessages error:', e);
        } finally {
            this._loadingOlder[chatId] = false;
        }
    }

    // Scroll-Trigger: nahe am oberen Rand aeltere Nachrichten holen
    maybeLoadOlder() {
        const container = $('#chatMessages')[0];
        if (!container) return;
        const chatId = VCS.state.chatActiveChatId;
        if (!chatId) return;
        if (container.scrollTop < 120 && this._hasMoreOlder[chatId] && !this._loadingOlder[chatId]) {
            this.loadOlderMessages(chatId);
        }
    }

    renderChatList() {
        const container = $('#chatList')[0];
        if (!container) {
            return;
        }

        if (VCS.state.chatChats.length === 0) {
            container.innerHTML = `<div class="chat-list-empty"><span>${t('chatNoChats')}</span></div>`;
            return;
        }

        // Filter: 1:1-Chats nur sichtbar, wenn Peer noch in approved Kontakten ist.
        // Solange Kontaktliste nicht geladen wurde (Bootstrap), nichts verstecken.
        const isShown = (c) => {
            if (c._isGroup || c.isGroup) return true;
            if (VCS.Chat.isBotChat(c.id)) return true;
            // Dating-Chat (Phase 5): Peer ist ein @handle, kein Kontakt — aber das Match ist
            // die Freigabe. Nicht durch den Kontakt-Filter fallen lassen.
            if (c.isDating || c._isDating) return true;
            const peer = VCS.chat?.peerPhone?.(c);
            if (peer && peer[0] === '@') return true;
            if (!VCS.state.chatContactsLoaded) return true;
            return peer && VCS.state.chatApprovedContacts.has(peer);
        };
        const visible = VCS.state.chatChats.filter(isShown);

        if (visible.length === 0) {
            container.innerHTML = `<div class="chat-list-empty"><span>${t('chatNoChats')}</span></div>`;
            return;
        }

        // Bot ohne bisherige Nachricht: ganz unten (sonst rutscht er per neuem created_at nach oben)
        const sortKey = (c) => {
            if (c.lastMessageAt) return c.lastMessageAt;
            if (VCS.Chat.isBotChat(c.id)) return 0;
            return c.created_at || 0;
        };
        const sorted = [...visible].sort((a, b) => sortKey(b) - sortKey(a));

        container.innerHTML = sorted.map(chat => {
            const isGroup = chat._isGroup || chat.isGroup || false;
            // Dating-Match: kein Nummern-Fallback -- die Nummer bleibt in der UI unsichtbar.
            const peerFallback = isGroup
                ? chat.name
                : (chat.isDatingMatch ? t('datingContactMatch') : VCS.chat?.peerPhone?.(chat));
            const peerName = escapeHtml(chat._peerName || peerFallback);
            const initial = (chat._peerName || peerFallback || '?').charAt(0).toUpperCase();
            let previewRaw = chat.lastMessageEncrypted
                ? '\u{1F512} ' + t('e2eNewMessage')
                : (chat.lastMessageText || '');
            const oldLabels = /^(Videonachricht|Video|Foto|Photo|Musik|Music|Sprachfoto|Voice Photo|Голосовое фото|Música|Musique|Photo vocale|Foto de voz|Foto vocale|video-message\.\w+)\s*/i;
            if (previewRaw.startsWith('\u{1F3AC}')) previewRaw = '\u{1F3AC} ' + t('chatVideo') + ' ' + previewRaw.slice(2).trim().replace(oldLabels, '');
            else if (previewRaw.startsWith('\u{1F4F7}')) previewRaw = '\u{1F4F7} ' + t('chatPhotoPreview') + ' ' + previewRaw.slice(2).trim().replace(oldLabels, '');
            else if (previewRaw.startsWith('\u{1F3B5}')) previewRaw = '\u{1F3B5} ' + t('chatMusic') + ' ' + previewRaw.slice(2).trim().replace(oldLabels, '');
            else if (previewRaw.startsWith('\u{1F4F8}')) previewRaw = '\u{1F4F8} ' + t('chatVoicephoto') + ' ' + previewRaw.slice(2).trim().replace(oldLabels, '');
            else if (previewRaw.startsWith('\u{1F3A4}')) previewRaw = '\u{1F3A4} ' + t('chatVoiceMessage');
            else if (this._stickerIdFromText(previewRaw)) previewRaw = '\u{1F3A8} ' + t('chatSticker');
            else if (this._gifIdFromText(previewRaw)) previewRaw = this._gifPreviewLabel(this._gifIdFromText(previewRaw));
            const preview = escapeHtml(chat._isBot ? stripMarkdown(previewRaw) : previewRaw.trim());
            const time = chat.lastMessageAt ? VCS.chat?.formatChatListTime?.(chat.lastMessageAt) : '';
            const unread = VCS.state.chatUnreadCounts[chat.id] || 0;
            const unreadBadge = unread > 0 ? `<span class="chat-unread-badge">${unread}</span>` : '';

            let onlineDot = '';
            let e2eBadge = '';
            let previewHtml;

            if (isGroup) {
                previewHtml = preview || '&nbsp;';
            } else {
                const peerPhone = VCS.chat?.peerPhone?.(chat);
                const isOnline = VCS.state.chatPresenceMap[peerPhone]?.online;
                onlineDot = isOnline ? `<span class="chat-list-online-dot"></span>` : '';
                const e2e = !!VCS.state.chatE2EEnabled[chat.id];
                e2eBadge = e2e
                    ? `<span class="chat-list-e2e-badge chat-list-e2e-badge--locked" data-tooltip-i18n="e2eEnabled"><svg viewBox="0 0 24 24" width="9" height="9" fill="currentColor"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg></span>`
                    : `<span class="chat-list-e2e-badge chat-list-e2e-badge--unlocked" data-tooltip-i18n="e2eDisabled"><svg viewBox="0 0 24 24" width="9" height="9" fill="currentColor"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 8.7-2.7"/></svg></span>`;
                const activity = VCS.state.chatActivityMap[peerPhone];
                if (activity?.type === 'recording') {
                    previewHtml = `<span class="contact-activity contact-activity--recording"><svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor" style="vertical-align:-1px"><path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/><path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/></svg> ${t('chatRecording')}</span>`;
                } else if (activity?.type === 'typing') {
                    previewHtml = `<span class="contact-activity contact-activity--typing">${t('chatTyping')}</span>`;
                } else {
                    previewHtml = preview || '&nbsp;';
                }
            }

            // Ungesendeter Entwurf? -> "Entwurf:" + Vorschau (ueberschreibt die Preview)
            let hasDraft = false;
            try {
                const draft = (localStorage.getItem(this._draftKey(chat.id)) || '').trim();
                if (draft) {
                    hasDraft = true;
                    previewHtml = `<span class="chat-list-draft">${t('chatDraft')}:</span> ${escapeHtml(draft)}`;
                }
            } catch (e) {}

            // Gelesen-Status (Häkchen) nur wenn ICH die letzte Nachricht gesendet habe;
            // bei einem Entwurf kein Haekchen anzeigen
            let statusIcon = '';
            if (!hasDraft && chat.lastMessageSender === VCS.state.chatMyPhone && chat.lastMessageAt) {
                if (chat.lastMessageStatus === 'read') {
                    statusIcon = '<span class="chat-list-status chat-list-status--read"><svg viewBox="0 0 24 11" width="24" height="11"><path d="M4 5.83L1.35 3.17l-.94.94L4 7.71l8-8-.94-.94z" fill="currentColor"/><path d="M4 5.83L1.35 3.17l-.94.94L4 7.71l8-8-.94-.94z" fill="currentColor" transform="translate(6)"/><path d="M4 5.83L1.35 3.17l-.94.94L4 7.71l8-8-.94-.94z" fill="currentColor" transform="translate(12)"/></svg></span>';
                } else if (chat.lastMessageStatus === 'delivered') {
                    statusIcon = '<span class="chat-list-status chat-list-status--delivered"><svg viewBox="0 0 18 11" width="18" height="11"><path d="M4 5.83L1.35 3.17l-.94.94L4 7.71l8-8-.94-.94z" fill="currentColor"/><path d="M4 5.83L1.35 3.17l-.94.94L4 7.71l8-8-.94-.94z" fill="currentColor" transform="translate(6.5)"/></svg></span>';
                } else {
                    statusIcon = '<span class="chat-list-status chat-list-status--sent"><svg viewBox="0 0 12 11" width="12" height="11"><path d="M4 5.83L1.35 3.17l-.94.94L4 7.71l8-8-.94-.94z" fill="currentColor"/></svg></span>';
                }
            }

            const avatarClass = isGroup ? 'group-list-avatar' : 'chat-list-avatar';
            const av = isGroup
                ? { html: (chat._peerAvatarHash ? `<img src="${VCS.Chat.API_BASE}/groups/${chat.id}/avatar/${chat._peerAvatarHash}" alt="">` : initial), hasAvatar: !!chat._peerAvatarHash }
                : (VCS.chat?.avatarHtml?.(initial, chat._peerAvatarHash) || { html: initial, hasAvatar: false });
            const groupBadge = isGroup ? `<span class="chat-list-group-badge"><svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor"><path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/></svg></span>` : '';
            return `<div class="chat-list-item cursor-pointer" data-chat-id="${escapeHtml(chat.id)}">
            <div class="${avatarClass}${av.hasAvatar ? ' has-avatar' : ''}">${av.html}${onlineDot}${groupBadge}${e2eBadge}</div>
            <div class="chat-list-body">
                <div class="chat-list-top-row">
                    <span class="chat-list-name">${peerName}</span>
                    <span class="chat-list-time">${time}</span>
                </div>
                <span class="chat-list-preview">${statusIcon}${previewHtml}</span>
            </div>
            ${unreadBadge}
        </div>`;
        }).join('');

        container.querySelectorAll('.chat-list-item').forEach(item => {
            item.onclick = () => {
                const chatId = item.dataset.chatId;
                VCS.chatSetup?.openConversation?.(chatId);
            };
            const avatar = item.querySelector('.chat-list-avatar.has-avatar');
            if (avatar) {
                avatar.style.cursor = 'pointer';
                avatar.onclick = (e) => {
                    e.stopPropagation();
                    const chatId = item.dataset.chatId;
                    const chat = VCS.state.chatChats.find(c => c.id === chatId);
                    if (chat?._peerAvatarHash) {
                        VCS.chat?.openAvatarViewer?.(chat._peerName || '', chat._peerAvatarHash);
                    }
                };
            }
        });

        if (VCS.nav?.isDesktopLayout() && VCS.state.chatActiveChatId) {
            container.querySelectorAll('.chat-list-item').forEach(el => {
                el.classList.toggle('chat-list-item--active', el.dataset.chatId === VCS.state.chatActiveChatId);
            });
        }
    }

    renderChatMessages() {
        const container = $('#chatMessages')[0];
        if (!container || !VCS.state.chatActiveChatId) {
            return;
        }

        const msgs = VCS.state.chatMessages[VCS.state.chatActiveChatId] || [];
        if (msgs.length === 0) {
            container.innerHTML = `<div class="chat-list-empty" style="min-height:200px"><span>${t('chatNoChats')}</span></div>`;
            return;
        }

        let html = '';
        let lastDate = '';

        const GROUP_GAP_MS = 5 * 60 * 1000;
        const groups = [];
        let currentGroup = null;

        const unreadMsgs = msgs.filter(m => m.senderPhone !== VCS.state.chatMyPhone && m.status !== 'read' && !m._sending);
        const firstUnreadId = unreadMsgs.length > 0 ? unreadMsgs[0].id : null;

        for (let i = 0; i < msgs.length; i++) {
            const msg = msgs[i];
            const prev = i > 0 ? msgs[i - 1] : null;
            const sameGroup = currentGroup && prev
                && msg.senderPhone === prev.senderPhone
                && (msg.timestamp - prev.timestamp) < GROUP_GAP_MS
                && VCS.chat?.formatDate?.(msg.timestamp) === VCS.chat?.formatDate?.(prev.timestamp);

            if (!sameGroup) {
                currentGroup = {
                    sender  : msg.senderPhone,
                    messages: [msg],
                    dateStr : VCS.chat?.formatDate?.(msg.timestamp)
                };
                groups.push(currentGroup);
            } else {
                currentGroup.messages.push(msg);
            }
        }

        for (const group of groups) {
            if (group.dateStr !== lastDate) {
                lastDate = group.dateStr;
                html += `<div class="chat-date-separator"><span>${escapeHtml(group.dateStr)}</span></div>`;
            }

            const isMine = group.sender === VCS.state.chatMyPhone;
            const sideClass = isMine ? 'chat-bubble-group--mine' : 'chat-bubble-group--peer';
            const bubbleClass = isMine ? 'chat-bubble--mine' : 'chat-bubble--peer';
            const len = group.messages.length;

            html += `<div class="chat-bubble-group ${sideClass}">`;

            for (let j = 0; j < len; j++) {
                const msg = group.messages[j];
                let posClass = '--single';
                if (len > 1) {
                    if (j === 0) {
                        posClass = '--first';
                    } else if (j === len - 1) {
                        posClass = '--last';
                    } else {
                        posClass = '--middle';
                    }
                }
                const sendingClass = msg._sending ? 'chat-bubble--sending'
                    : (msg._streaming ? 'chat-bubble--streaming' : '');
                const mediaClass = msg.mediaUrl ? 'chat-bubble--has-media' : '';
                const showMeta = true;

                if (msg.id === firstUnreadId) {
                    html += `<div class="chat-unread-separator"><span>${t('chatNewMessages')}</span></div>`;
                }

                html += `<div class="chat-bubble ${bubbleClass} chat-bubble${posClass} ${sendingClass} ${mediaClass}" data-msg-id="${escapeHtml(msg.id)}">`;
                html += `<div class="chat-swipe-reply-icon"><svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 14l-4-4 4-4"/><path d="M5 10h11a4 4 0 010 8h-1"/></svg></div>`;
                html += this.renderSingleBubbleContent(msg, showMeta);
                html += this.renderReactionBadges(msg);
                html += `</div>`;
            }

            html += `</div>`;
        }

        // Context-Menu schliessen bevor DOM ersetzt wird
        this.hideContextMenu();

        // Bereits geladene Medien ueber den Neuaufbau retten, sonst laden alle
        // Bilder, Video-Player und GIFs bei jedem Render neu (Flackern).
        const keptMedia = this._collectRenderedMedia(container);
        container.innerHTML = html;
        this._restoreRenderedMedia(container, keptMedia);
        this.bindBubbleEvents(container);
        VCS.chatVideo?.mountVideoPostersIn?.(container);
        this._applyCachedMedia(container);
        // Alter Observer haengt an den ersetzten Knoten -> neu aufsetzen.
        this._chatGifObserver?.disconnect();
        this._chatGifObserver = this._observeGifVideos(container, null);
    }

    // Merkt fertig geladene Medien-Elemente je Nachricht + Quelle.
    // Schluessel muessen zu _restoreRenderedMedia passen.
    _collectRenderedMedia(container) {
        const kept = new Map();
        container.querySelectorAll('.chat-bubble[data-msg-id]').forEach(bubble => {
            const msgId = bubble.dataset.msgId;
            bubble.querySelectorAll('img[data-src]').forEach(img => {
                if (img.hasAttribute('data-media-src') || !img.complete || !img.naturalWidth) return;
                kept.set(msgId + '|img|' + img.getAttribute('data-src'), img);
            });
            bubble.querySelectorAll('.chat-video-player-wrap[data-vp-key]').forEach(wrap => {
                kept.set(msgId + '|vp|' + wrap.dataset.vpKey, wrap);
            });
            bubble.querySelectorAll('.chat-gif-video').forEach(video => {
                kept.set(msgId + '|gif|' + video.getAttribute('src'), video);
            });
        });
        return kept;
    }

    // Setzt gemerkte Elemente anstelle der frisch erzeugten ein, wenn
    // Nachricht und Quelle gleich geblieben sind.
    _restoreRenderedMedia(container, kept) {
        if (!kept.size) return;
        container.querySelectorAll('.chat-bubble[data-msg-id]').forEach(bubble => {
            const msgId = bubble.dataset.msgId;
            bubble.querySelectorAll('img[data-src]').forEach(img => {
                const old = kept.get(msgId + '|img|' + img.getAttribute('data-src'));
                if (old) img.replaceWith(old);
            });
            bubble.querySelectorAll('.chat-video-poster[data-src]').forEach(poster => {
                const key = poster.getAttribute('data-src') + '|' + (poster.getAttribute('data-karaoke') || '');
                const old = kept.get(msgId + '|vp|' + key);
                if (old) poster.replaceWith(old);
            });
            bubble.querySelectorAll('.chat-gif-video').forEach(video => {
                const old = kept.get(msgId + '|gif|' + video.getAttribute('src'));
                if (old) video.replaceWith(old);
            });
        });
    }

    renderSingleBubbleContent(msg, showMeta) {
        const isMine = msg.senderPhone === VCS.state.chatMyPhone;

        // Gruppen: Absendername anzeigen
        let senderHtml = '';
        if (VCS.state.chatActiveIsGroup && !isMine && showMeta) {
            const senderName = msg.senderName || this._getGroupSenderName(msg.senderPhone);
            senderHtml = `<div class="chat-bubble-sender">${escapeHtml(senderName)}</div>`;
        }

        let replyHtml = '';
        if (msg.replyTo) {
            const replyName = msg.replyTo.senderPhone === VCS.state.chatMyPhone ? (VCS.state.chatMyName || t('you')) : (this.getPeerName() || msg.replyTo.senderPhone);
            let replyText;
            if (msg.replyTo.encrypted) {
                // Klartext aus der bereits entschluesselten Originalnachricht im Chat-State holen
                const chatMsgs = VCS.state.chatMessages[msg.chatId] || [];
                const original = chatMsgs.find(m => m.id === msg.replyTo.id);
                replyText = msg.replyTo._decryptedText || original?._decryptedText || t('e2eNewMessage');
            } else {
                replyText = msg.replyTo.originalText || '';
            }
            // Antwort auf einen Sticker: lesbares Label statt Roh-Token zeigen.
            if (this._stickerIdFromText(replyText)) {
                replyText = '\u{1F3A8} ' + t('chatSticker');
            } else if (this._gifIdFromText(replyText)) {
                replyText = this._gifPreviewLabel(this._gifIdFromText(replyText));
            }
            replyHtml = `<div class="chat-bubble-reply" data-scroll-to="${escapeHtml(msg.replyTo.id)}">
            <div class="chat-bubble-reply-name">${escapeHtml(replyName)}</div>
            <div class="chat-bubble-reply-text">${escapeHtml(replyText)}</div>
        </div>`;
        }
        if (msg.isForwarded) {
            replyHtml = `<div class="chat-bubble-forwarded"><svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" style="vertical-align:-2px;margin-right:4px;opacity:0.7"><polyline points="15 17 20 12 15 7"/><path d="M4 18v-2a4 4 0 014-4h12"/></svg>${escapeHtml(t('chatForwarded'))}</div>` + replyHtml;
        }

        let statusHtml = '';
        if (isMine && showMeta) {
            const isGroup = VCS.state.chatActiveIsGroup;
            const readerCount = VCS.state._groupReaders?.[msg.id]?.size || 0;

            if (msg.status === 'read') {
                const readAtAttr = msg._readAt ? ` data-read-at="${msg._readAt}"` : '';
                const delAtAttr = msg._deliveredAt ? ` data-delivered-at="${msg._deliveredAt}"` : '';
                statusHtml = `<span class="chat-bubble-status chat-bubble-status--read" data-status="read"${readAtAttr}${delAtAttr}>&#10003;&#10003;&#10003;</span>`;
            } else if (msg.status === 'delivered' || readerCount > 0) {
                const partialClass = (isGroup && readerCount > 0) ? ' chat-bubble-status--partial' : ' chat-bubble-status--delivered';
                const delAtAttr = msg._deliveredAt ? ` data-delivered-at="${msg._deliveredAt}"` : '';
                statusHtml = `<span class="chat-bubble-status${partialClass}" data-status="delivered"${delAtAttr}>&#10003;&#10003;</span>`;
            } else {
                statusHtml = `<span class="chat-bubble-status" data-status="sent">&#10003;</span>`;
            }

            // Gruppen: Tappbare Leser-Info (immer sichtbar)
            if (isGroup) {
                const t2 = (k) => VCS.i18n?.t(k) || k;
                const eyeIcon = '<svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" style="vertical-align:-1px"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>';
                const label = readerCount > 0
                    ? `${eyeIcon} ${readerCount}`
                    : `${eyeIcon}`;
                statusHtml += `<span class="chat-read-info" data-readers-msg-id="${msg.id}">${label}</span>`;
            }
        }

        const editedHtml = msg.editedAt ? `<span class="chat-bubble-edited">${t('chatEdited')}</span>` : '';
        const e2eBadge = msg.encrypted ? `<span class="msg-e2e-badge" title="${escapeHtml(t('e2eEnabled'))}"><svg viewBox="0 0 24 24" width="10" height="10" fill="currentColor"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg></span>` : '';
        const timeHtml = showMeta ? `<div class="chat-bubble-meta"><span class="chat-bubble-time">${VCS.chat?.formatTime?.(msg.timestamp)}</span>${e2eBadge}${editedHtml}${statusHtml}</div>` : '';

        // Eigener Sticker: als grosses Bild ohne Textblase rendern (wie Jumbo-Emoji).
        const stickerId = this._stickerIdFromText(msg._decryptedText || msg.originalText);
        if (stickerId && !msg.mediaUrl && !msg.audioUrl) {
            const surl = this._stickerUrl(stickerId);
            if (surl) {
                // Pop-Effekt nur, wenn diese Nachricht ausdruecklich als NEU
                // hinzugefuegt vorgemerkt wurde (eigenes Senden / Live-Empfang).
                // Beim Laden der Historie oder bei Re-Renders (Lese-Status,
                // Media-Decrypt usw.) ist die ID nicht vorgemerkt -> kein Effekt.
                // Die Marke wird hier konsumiert, damit der Effekt genau einmal
                // laeuft und nicht bei jedem weiteren Render erneut.
                let popClass = '';
                if (msg.id && this._stickerPopPending.has(msg.id)) {
                    this._stickerPopPending.delete(msg.id);
                    popClass = ' chat-sticker--pop';
                }
                return `${senderHtml}${replyHtml}<div class="chat-sticker-wrap"><img class="chat-sticker${popClass}" src="${escapeHtml(surl)}" alt="Sticker" draggable="false"></div>${timeHtml}`;
            }
        }

        // GIF (stumme MP4-Schleife): Video ohne Steuerung statt Textblase.
        // Abgespielt wird nur, was sichtbar ist (_observeGifVideos).
        const gifId = this._gifIdFromText(msg._decryptedText || msg.originalText);
        if (gifId && !msg.mediaUrl && !msg.audioUrl) {
            const gif = this._gifEntry(gifId);
            const inner = gif
                ? this._gifVideoHtml(gif, true)
                : `<div class="chat-gif-missing">${escapeHtml(t('chatGif'))}</div>`;
            return `${senderHtml}${replyHtml}<div class="chat-gif-wrap">${inner}</div>${timeHtml}`;
        }

        if (msg.mediaUrl) {
            let mediaHtml = '';
            const mediaSrc = (msg.mediaUrl === 'pending' && msg._localBlobUrl) ? msg._localBlobUrl : msg.mediaUrl;
            const pendingClass = msg.mediaUrl === 'pending' ? ' style="opacity:0.5"' : '';

            if (msg.mediaUrl === 'pending' && !msg._localBlobUrl) {
                mediaHtml = `<div class="chat-bubble-text" style="opacity:0.5">${escapeHtml(msg.mediaFileName || '...')}...</div>`;
            } else if (msg.encrypted && msg.mediaUrl !== 'pending' && !msg._decryptedMediaUrl && !msg._localBlobUrl && !msg._decryptFailed) {
                mediaHtml = `<div class="chat-bubble-text chat-bubble-text--encrypted"><svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" style="vertical-align:-2px;margin-right:4px;opacity:0.6"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>${escapeHtml(t('e2eNewMessage'))}</div>`;
                VCS.e2e?.getDecryptedMediaUrl?.(msg).then(() => {
                    if (VCS.state.chatActiveChatId === msg.chatId) {
                        this.renderChatMessages();
                        this.scrollToBottom();
                    }
                });
            } else if (msg.mediaType === 'image') {
                const imgSrc = msg._decryptedMediaUrl || mediaSrc;
                const useDirectSrc = imgSrc.startsWith('blob:') || msg._localBlobUrl;
                mediaHtml = `<div class="chat-bubble-media"${pendingClass}><img ${useDirectSrc ? 'src' : 'data-media-src'}="${escapeHtml(imgSrc)}" alt="" data-action="lightbox" data-src="${escapeHtml(imgSrc)}"></div>`;
            } else if (msg.mediaType === 'voicephoto') {
                const isFromOther = msg.senderPhone !== VCS.state.chatMyPhone;
                const langDiffers = isFromOther && msg.senderLang && msg.senderLang !== (VCS.state.currentLang || 'de');
                const isFresh = (Date.now() - msg.timestamp) < 30000;
                const needsTranslation = langDiffers && isFresh && msg._vpWords && msg._vpWords.length > 0 && !msg._vpWordsTranslated;
                if (needsTranslation) {
                    mediaHtml = `<div class="chat-voicephoto"${pendingClass} data-msg-id="${escapeHtml(msg.id)}">
                    <div class="chat-voicephoto-translating">
                        <div class="chat-photo-capture-spinner"></div>
                        <span>${escapeHtml(t('chatVoicephotoTranslating') || 'Wird übersetzt…')}</span>
                    </div>
                </div>`;
                    return `${senderHtml}${replyHtml}${mediaHtml}${timeHtml}`;
                }
                const imgSrc = msg._decryptedMediaUrl || mediaSrc;
                const audioSrc = msg.audioUrl || '';
                const dur = VCS.chat?.formatDuration?.(msg.audioDurationMs || 0);
                const hasAudio = !!msg.audioUrl;
                const vpCaption = msg._decryptedText || msg.originalText || '';
                const vpDirectSrc = imgSrc.startsWith('blob:') || msg._localBlobUrl;
                const hintOverlay = hasAudio ? `<div class="chat-voicephoto-hint" data-action="toggle-vp-hint">
                    <svg viewBox="0 0 24 24" width="32" height="32" fill="rgba(255,255,255,0.9)"><polygon points="6,3 20,12 6,21"></polygon></svg>
                    <span>${escapeHtml(t('chatVoicephotoHint') || 'Abspielen um den Text zu hören')}</span>
                </div>` : '';
                const captionOverlay = vpCaption ? `<div class="chat-voicephoto-caption hidden">${escapeHtmlWithBreaks(vpCaption)}</div>` : '';
                const controlsHtml = hasAudio ? `
                <div class="chat-voicephoto-controls">
                    <button class="chat-voicephoto-play" type="button" data-action="play-voicephoto" data-id="${escapeHtml(msg.id)}">
                        <svg class="chat-voicephoto-icon-play" viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><polygon points="6,3 20,12 6,21"></polygon></svg>
                        <svg class="chat-voicephoto-icon-pause hidden" viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><rect x="5" y="3" width="4" height="18" rx="1"></rect><rect x="15" y="3" width="4" height="18" rx="1"></rect></svg>
                    </button>
                    <div class="chat-voicephoto-progress-wrap">
                        <div class="chat-voicephoto-progress" data-vp-progress="${escapeHtml(msg.id)}" data-action="seek-voicephoto" data-id="${escapeHtml(msg.id)}">
                            <div class="chat-voicephoto-progress-bar"></div>
                        </div>
                    </div>
                    <span class="chat-voicephoto-dur" data-vp-dur="${escapeHtml(msg.id)}">${dur}</span>
                </div>
                <audio class="chat-voicephoto-audio" preload="none" src="${escapeHtml(audioSrc)}" data-vp-audio="${escapeHtml(msg.id)}"></audio>` : '';
                mediaHtml = `<div class="chat-voicephoto"${pendingClass} data-msg-id="${escapeHtml(msg.id)}">
                <div class="chat-voicephoto-img-wrap">
                    <img class="chat-voicephoto-img" ${vpDirectSrc ? 'src' : 'data-media-src'}="${escapeHtml(imgSrc)}" alt="" data-action="lightbox" data-src="${escapeHtml(imgSrc)}">
                    ${hintOverlay}
                    ${captionOverlay}
                </div>
                ${controlsHtml}
            </div>`;
            } else if (msg.mediaType === 'video') {
                // Solange die Datei noch laeuft: echter Fortschritt. Bei 100 %
                // faellt es durch auf die Verarbeitungs-Bubble darunter - fuer
                // die Server-Phase gibt es keine Prozentzahl.
                if (msg._sending && typeof msg._uploadProgress === 'number' && msg._uploadProgress < 100) {
                    mediaHtml = this.renderUploadProgress(msg);
                    return `${senderHtml}${replyHtml}${mediaHtml}${timeHtml}`;
                }
                if (VCS.chatVideo?.isDeferredVideoMessage?.(msg)) {
                    mediaHtml = `<div class="chat-video-processing"${pendingClass}>
                    <span class="chat-video-processing-clock" aria-hidden="true">
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2">
                            <circle cx="12" cy="12" r="8"></circle>
                            <path d="M12 7v5l3 2"></path>
                        </svg>
                    </span>
                    <span>${escapeHtml(t('chatVideoProcessing'))}</span>
                </div>`;
                    return `${senderHtml}${replyHtml}${mediaHtml}${timeHtml}`;
                }
                // E2E: warten bis Video entschluesselt ist, dann re-render
                if (msg.encrypted && msg.mediaUrl !== 'pending' && !msg._decryptedMediaUrl && !msg._localBlobUrl && !msg._decryptFailed) {
                    mediaHtml = `<div class="chat-bubble-text chat-bubble-text--encrypted"><svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" style="vertical-align:-2px;margin-right:4px;opacity:0.6"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>${escapeHtml(t('e2eNewMessage'))}</div>`;
                    VCS.e2e?.getDecryptedMediaUrl?.(msg).then(() => {
                        if (VCS.state.chatActiveChatId === msg.chatId) {
                            this.renderChatMessages();
                            this.scrollToBottom();
                        }
                    });
                    return `${senderHtml}${replyHtml}${mediaHtml}${timeHtml}`;
                }
                const translateOn = !VCS.state.chatE2EEnabled[VCS.state.chatActiveChatId];
                const hasTranslatedVideo = !!msg.translatedVideoUrl;
                const isSameLangCaption = hasTranslatedVideo && msg.translatedVideoLang === msg.senderLang;
                const hasVideoWords = !!(msg._videoWords && msg._videoWords.length);
                // Neue Videos (mit Karaoke-Woertern): Original + Karaoke-Overlay
                // Alte Videos (ohne Woerter): altes Verhalten mit captioned Video
                let vidSrc, useKaraoke;
                if (hasVideoWords) {
                    // Neues Verhalten: immer Original-Video, immer Karaoke
                    // Toggle steuert nur welche Woerter (Original vs Translated)
                    vidSrc = msg._decryptedMediaUrl || mediaSrc;
                    useKaraoke = true;
                } else {
                    // Altes Verhalten: Toggle zwischen Original/Captioned Video
                    const useTranslated = hasTranslatedVideo && (isSameLangCaption || (translateOn && msg._preferTranslatedVideo !== false));
                    vidSrc = useTranslated ? msg.translatedVideoUrl : (msg._decryptedMediaUrl || mediaSrc);
                    useKaraoke = false;
                }
                const saveBtnDisabled = msg.mediaUrl === 'pending' ? ' disabled' : '';
                let showTranslatedDefault;
                if (hasVideoWords) {
                    const isMine = msg.senderPhone === VCS.state.chatMyPhone;
                    showTranslatedDefault = isMine ? msg._preferTranslatedVideo === true : msg._preferTranslatedVideo !== false;
                } else {
                    showTranslatedDefault = hasTranslatedVideo && (isSameLangCaption || (translateOn && msg._preferTranslatedVideo !== false));
                }
                // YouTube-style CC toggle: button always visible when karaoke
                // words are available.
                // - Cross-language: ALWAYS ON by default (user toggle is session-only,
                //   reload restores ON — translations need captions to make sense).
                // - Same-language: user pref from localStorage, default OFF.
                const myLang = VCS.state.currentLang || 'de';
                const sameLang = !!msg.senderLang && msg.senderLang === myLang;
                if (hasVideoWords && typeof msg._captionsOn !== 'boolean') {
                    if (sameLang) {
                        msg._captionsOn = localStorage.getItem('vcs_cc_pref_samelang') === '1';
                    } else {
                        msg._captionsOn = true;
                    }
                }
                const translateToggleHtml = (hasTranslatedVideo && !isSameLangCaption) ? `
                    <button class="chat-video-toggle-btn ${!showTranslatedDefault ? 'chat-video-toggle-btn--active' : ''}"
                            type="button"
                            data-action="video-variant-orig" data-id="${escapeHtml(msg.id)}">${escapeHtml(t('videoOriginal'))}</button>
                    <button class="chat-video-toggle-btn ${showTranslatedDefault ? 'chat-video-toggle-btn--active' : ''}"
                            type="button"
                            data-action="video-variant-trans" data-id="${escapeHtml(msg.id)}">${escapeHtml(t('videoTranslated'))}</button>` : '';
                const toggleHtml = translateToggleHtml
                    ? `<div class="chat-video-toggle-row">${translateToggleHtml}</div>`
                    : '';
                mediaHtml = `<div class="chat-bubble-media"${pendingClass}>
                <div class="chat-video-poster" data-video-thumb="${escapeHtml(vidSrc)}" data-action="play-video" data-src="${escapeHtml(vidSrc)}" data-karaoke="${useKaraoke ? escapeHtml(msg.id) : ''}"${msg.mediaDurationMs ? ` data-duration-ms="${escapeHtml(String(msg.mediaDurationMs))}"` : ''}${msg.mediaThumbnailUrl ? ` data-poster="${escapeHtml(msg.mediaThumbnailUrl)}"` : ''}>
                    <svg viewBox="0 0 24 24" width="48" height="48" fill="rgba(255,255,255,0.9)"><polygon points="6,3 20,12 6,21"></polygon></svg>
                </div>
                ${toggleHtml}
                <button class="chat-media-save-btn" type="button" title="${escapeHtml(t('saveVideo'))}" aria-label="${escapeHtml(t('saveVideo'))}" data-action="save-video" data-id="${escapeHtml(msg.id)}"${saveBtnDisabled}>
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M12 3v12"></path>
                        <path d="M7 10l5 5 5-5"></path>
                        <path d="M4 20h16"></path>
                    </svg>
                </button>
            </div>`;
            } else if (msg.mediaType === 'music') {
                mediaHtml = `<div class="chat-bubble-media-name"><svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>${escapeHtml(msg.mediaFileName || 'audio.mp3')}</div>`;
                // Player im SecondRadio-Stil: Wellenform als Spulleiste, Zeit, Tempo (chat-voice.js)
                mediaHtml += VCS.chatVoice?.musicPlayerHtml?.(msg, pendingClass) || '';
            } else if (msg.mediaType === 'document') {
                // Dokument-Kachel: Icon, Dateiname, Groesse und Download-Button (keine Inline-Vorschau)
                const docName = escapeHtml(msg.mediaFileName || 'dokument.pdf');
                const docSize = msg.mediaSizeBytes ? (VCS.chatMedia?._formatFileSize?.(msg.mediaSizeBytes) || '') : '';
                // "PDF ·" vor der Groesse: Bei langen Dateinamen wird der Name
                // abgeschnitten, der Typ bleibt so trotzdem lesbar. Gleich wie
                // in der Flutter-App.
                const docMeta = docSize ? 'PDF · ' + escapeHtml(docSize) : 'PDF';
                // Material-Icon "picture_as_pdf" (google/material-design-icons,
                // src/image/picture_as_pdf/materialicons/24px.svg) — identisch
                // mit dem Icon der Flutter-App. Anders als die uebrigen Icons
                // hier ein Fill-Pfad, kein Stroke.
                mediaHtml = `<div class="chat-bubble-doc" data-action="view-document" data-id="${escapeHtml(msg.id)}"${pendingClass}>
                <svg class="chat-bubble-doc-icon" viewBox="0 0 24 24" width="28" height="28" fill="currentColor" aria-hidden="true">
                    <path d="M20 2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-8.5 7.5c0 .83-.67 1.5-1.5 1.5H9v2H7.5V7H10c.83 0 1.5.67 1.5 1.5v1zm5 2c0 .83-.67 1.5-1.5 1.5h-2.5V7H15c.83 0 1.5.67 1.5 1.5v3zm4-3H19v1h1.5V11H19v2h-1.5V7h3v1.5zM9 9.5h1v-1H9v1zM4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm10 5.5h1v-3h-1v3z"/>
                </svg>
                <div class="chat-bubble-doc-meta">
                    <div class="chat-bubble-doc-name">${docName}</div>
                    <div class="chat-bubble-doc-size">${docMeta}</div>
                </div>
                <button class="chat-bubble-doc-dl" type="button" title="${escapeHtml(t('chatDownload'))}" aria-label="${escapeHtml(t('chatDownload'))}" data-action="save-document" data-id="${escapeHtml(msg.id)}">
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M12 3v12"></path>
                        <path d="M7 10l5 5 5-5"></path>
                        <path d="M4 20h16"></path>
                    </svg>
                </button>
            </div>`;
            }

            let captionHtml = '';
            const isSameLang = (msg.mediaType === 'video' && !!msg.translatedVideoUrl && msg.translatedVideoLang === msg.senderLang);
            const isVoicephoto = msg.mediaType === 'voicephoto';
            const isVideoWithKaraoke = msg.mediaType === 'video' && msg._videoWords && msg._videoWords.length;
            if (!isSameLang) {
                if (!isVoicephoto && !isVideoWithKaraoke) {
                    const captionText = msg._decryptedText || msg.originalText;
                    if (captionText && !(msg.encrypted && !msg._decryptedText && msg.senderPhone !== VCS.state.chatMyPhone)) {
                        captionHtml = `<div class="chat-bubble-text">${escapeHtmlWithBreaks(captionText)}</div>`;
                    }
                }
                if (msg.translatedText && !msg.encrypted && !isVoicephoto && !isVideoWithKaraoke) {
                    captionHtml += `<div class="chat-auto-translated-pill">${t('chatAutoTranslated')}</div>`;
                    captionHtml += `<div class="chat-bubble-translation">${escapeHtmlWithBreaks(msg.translatedText)}</div>`;
                } else if (!isVoicephoto && !isVideoWithKaraoke && this._shouldShowTranslatingPlaceholder(msg)) {
                    captionHtml += `<div class="chat-bubble-translation chat-bubble-translation--pending">${escapeHtml(t('chatTranslating'))} <span class="chat-translating-dots"><span></span><span></span><span></span></span></div>`;
                }
            }
            return `${senderHtml}${replyHtml}${mediaHtml}${captionHtml}${timeHtml}`;
        }

        if (msg.audioUrl && msg.audioUrl !== 'pending') {
            const duration = VCS.chat?.formatDuration?.(msg.audioDurationMs);
            let voiceHtml = '';
            voiceHtml += `<div class="chat-voice-label">${t('chatVoiceOriginal')}</div>`;
            voiceHtml += `<div class="chat-voice-player">
            <button class="chat-voice-btn" data-action="play-audio" data-id="${escapeHtml(msg.id)}" data-variant="original" type="button" data-voice-btn="${escapeHtml(msg.id)}-original">
                <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><polygon points="6,4 20,12 6,20"></polygon></svg>
            </button>
            <div class="chat-voice-waveform" data-voice-waveform="${escapeHtml(msg.id)}-original">${this.renderWaveformBars(msg.id)}</div>
            <span class="chat-voice-duration" data-voice-dur="${escapeHtml(msg.id)}-original">${duration}</span>
        </div>`;

            if (msg.translatedAudioUrl) {
                const tDuration = VCS.chat?.formatDuration?.(msg.translatedAudioDurationMs || msg.audioDurationMs);
                voiceHtml += `<div class="chat-voice-label">${t('chatVoiceTranslation')}</div>`;
                voiceHtml += `<div class="chat-voice-player">
                <button class="chat-voice-btn chat-voice-btn--translated" data-action="play-audio" data-id="${escapeHtml(msg.id)}" data-variant="translated" type="button" data-voice-btn="${escapeHtml(msg.id)}-translated">
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><polygon points="6,4 20,12 6,20"></polygon></svg>
                </button>
                <div class="chat-voice-waveform chat-voice-waveform--translated" data-voice-waveform="${escapeHtml(msg.id)}-translated">${this.renderWaveformBars(msg.id + '_t')}</div>
                <span class="chat-voice-duration" data-voice-dur="${escapeHtml(msg.id)}-translated">${tDuration}</span>
            </div>`;
            }

            if (msg.transcription) {
                voiceHtml += `<div class="chat-bubble-text" style="margin-top:0.3rem;font-size:0.82rem;opacity:0.7">${escapeHtmlWithBreaks(msg.transcription)}</div>`;
            }
            if (msg.translatedText) {
                voiceHtml += `<div class="chat-auto-translated-pill">${t('chatAutoTranslated')}</div>`;
                voiceHtml += `<div class="chat-bubble-translation">${escapeHtmlWithBreaks(msg.translatedText)}</div>`;
            } else if (msg.transcription && this._shouldShowTranslatingPlaceholder(msg)) {
                voiceHtml += `<div class="chat-bubble-translation chat-bubble-translation--pending">${escapeHtml(t('chatTranslating'))} <span class="chat-translating-dots"><span></span><span></span><span></span></span></div>`;
            }
            return `${senderHtml}${replyHtml}${voiceHtml}${timeHtml}`;
        } else if (msg.audioUrl === 'pending') {
            return `${senderHtml}${replyHtml}<div class="chat-bubble-text" style="opacity:0.5">${t('chatVoiceMessage')}...</div>${timeHtml}`;
        } else {
            const displayText = msg._decryptedText || msg.originalText;
            const isUndecrypted = msg.encrypted && !msg._decryptedText && msg.senderPhone !== VCS.state.chatMyPhone;
            let textHtml = '';
            if (isUndecrypted) {
                textHtml = `<div class="chat-bubble-text chat-bubble-text--encrypted"><svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" style="vertical-align:-2px;margin-right:4px;opacity:0.6"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>${escapeHtml(t('e2eNewMessage'))}</div>`;
            } else if (msg.translatedText && !msg.encrypted) {
                const isIncoming = msg.senderPhone !== VCS.state.chatMyPhone;
                if (isIncoming) {
                    // Empfaenger: Uebersetzung oben (gross), Original unten (klein)
                    textHtml = `<div class="chat-bubble-text">${escapeHtmlWithBreaks(msg.translatedText)}</div>`;
                    textHtml += `<div class="chat-auto-translated-pill">${t('chatAutoTranslated')}</div>`;
                    textHtml += `<div class="chat-bubble-original">${escapeHtmlWithBreaks(displayText)}</div>`;
                } else {
                    // Sender: Original oben (gross), Uebersetzung unten (klein)
                    textHtml = `<div class="chat-bubble-text">${escapeHtmlWithBreaks(displayText)}</div>`;
                    textHtml += `<div class="chat-auto-translated-pill">${t('chatTranslation')}</div>`;
                    textHtml += `<div class="chat-bubble-original">${escapeHtmlWithBreaks(msg.translatedText)}</div>`;
                }
            } else {
                const isIncoming = msg.senderPhone !== VCS.state.chatMyPhone;
                const showPlaceholder = !isUndecrypted && this._shouldShowTranslatingPlaceholder(msg);
                if (isIncoming && showPlaceholder) {
                    // Empfangene Nachricht: nur Platzhalter, Original wird versteckt bis Uebersetzung da ist
                    textHtml = `<div class="chat-bubble-text chat-bubble-translation--pending">${escapeHtml(t('chatTranslatingIncoming'))} <span class="chat-translating-dots"><span></span><span></span><span></span></span></div>`;
                } else {
                    textHtml = `<div class="chat-bubble-text">${msg.senderPhone === VCS.Chat.BOT_PHONE ? parseMarkdown(displayText) : escapeHtmlWithBreaks(displayText)}</div>`;
                    if (showPlaceholder) {
                        textHtml += `<div class="chat-bubble-translation chat-bubble-translation--pending">${escapeHtml(t('chatTranslating'))} <span class="chat-translating-dots"><span></span><span></span><span></span></span></div>`;
                    }
                }
            }
            const ytId = extractYouTubeId(displayText);
            if (ytId) {
                textHtml += `<div class="chat-bubble-youtube"><iframe src="https://www.youtube-nocookie.com/embed/${ytId}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe></div>`;
            }
            const mapsUrl = !ytId && extractGoogleMapsUrl(displayText);
            if (mapsUrl) {
                textHtml += `<div class="chat-bubble-maps"><iframe src="https://maps.google.com/maps?q=${encodeURIComponent(mapsUrl)}&output=embed" allowfullscreen loading="lazy" referrerpolicy="no-referrer-when-downgrade"></iframe></div>`;
            }
            return `${senderHtml}${replyHtml}${textHtml}${timeHtml}`;
        }
    }

    renderWaveformBars(seed) {
        return VCS.chatVoice?.renderWaveformBars?.(seed) || '';
    }

    getPeerName() {
        const chat = VCS.state.chatChats.find(c => c.id === VCS.state.chatActiveChatId);
        return chat?._peerName || null;
    }

    _getGroupSenderName(phone) {
        // Aus Kontakten oder MembersBar
        const contact = (VCS.state.chatMergedContacts || []).find(c => c.phoneNumber === phone);
        if (contact?.displayName) return contact.displayName;
        const saved = (VCS.state.chatSavedContacts || []).find(c => c.phoneNumber === phone);
        if (saved?.displayName) return saved.displayName;
        return phone;
    }

    async _applyCachedMedia(container) {
        const images = container.querySelectorAll('[data-media-src]');
        for (const img of images) {
            const src = img.getAttribute('data-media-src');
            if (!src) continue;
            try {
                const resolved = VCS.mediaCache
                    ? await VCS.mediaCache.get(src)
                    : src;
                if (img.isConnected) {
                    img.src = resolved;
                    img.removeAttribute('data-media-src');
                    // Lightbox-onclick auch aktualisieren
                    if (resolved !== src) {
                        const onclick = img.getAttribute('onclick');
                        if (onclick && onclick.includes(src)) {
                            img.setAttribute('onclick', onclick.split(src).join(resolved));
                        }
                    }
                }
            } catch (_) {
                if (img.isConnected) {
                    img.src = src;
                    img.removeAttribute('data-media-src');
                }
            }
        }

        // Video-Thumbnails laden
        if (VCS.mediaCache) {
            const posters = container.querySelectorAll('[data-video-thumb]');
            for (const el of posters) {
                const videoUrl = el.getAttribute('data-video-thumb');
                if (!videoUrl) continue;
                try {
                    const thumbUrl = await VCS.mediaCache.getThumbnail(videoUrl);
                    if (thumbUrl && el.isConnected) {
                        el.style.backgroundImage = `url(${thumbUrl})`;
                        el.style.backgroundSize = 'cover';
                        el.style.backgroundPosition = 'center';
                    }
                } catch (_) {}
            }
        }
    }

    scrollToBottom() {
        const container = $('#chatMessages')[0];
        if (container) {
            const doScroll = () => { container.scrollTop = container.scrollHeight; };
            requestAnimationFrame(() => {
                doScroll();
                // Zweiter Scroll nach kurzem Delay fuer Bilder/Medien die spaeter laden
                setTimeout(doScroll, 150);
            });
        }
    }

    markAsRead(chatId) {
        const msgs = VCS.state.chatMessages[chatId] || [];
        const unreadIds = msgs
            .filter(m => m.senderPhone !== VCS.state.chatMyPhone && m.status !== 'read')
            .map(m => m.id)
            .filter(id => !id.startsWith('temp_'));

        // Immer markAll senden: der Server raeumt damit auch ungelesene Nachrichten
        // ausserhalb des geladenen Pagination-Fensters ab. Sonst bleibt der Badge
        // haengen, weil der Client diese IDs nie kennt und nie melden kann.
        VCS.chat?.wsSend?.({
            type      : 'messages-read',
            chatId,
            messageIds: unreadIds,
            markAll   : true
        });
        msgs.forEach(m => {
            if (m.senderPhone !== VCS.state.chatMyPhone && m.status !== 'read') {
                m.status = 'read';
            }
        });
        this._clearUnreadUI(chatId);

        // OS-Notifications fuer diesen Chat schliessen (Service Worker)
        this._dismissNotifications(chatId);
    }

    // Multi-Device: anderes Geraet hat den Chat gelesen
    handleReadSync(chatId) {
        if (!chatId) return;
        VCS.state.chatUnreadCounts[chatId] = 0;

        // Lokale Nachrichten als gelesen markieren
        const msgs = VCS.state.chatMessages[chatId] || [];
        msgs.forEach(m => {
            if (m.senderPhone !== VCS.state.chatMyPhone && m.status !== 'read') {
                m.status = 'read';
            }
        });

        this._clearUnreadUI(chatId);
        this._dismissNotifications(chatId);

        // Unread-Separator entfernen falls dieser Chat gerade offen ist
        if (VCS.state.chatActiveChatId === chatId) {
            document.querySelectorAll('.chat-unread-separator').forEach(el => el.remove());
        }

        console.log('[chat] read-sync: chatId=%s — badges cleared', chatId);
    }

    // Badge + Unread-Count im UI zuruecksetzen
    _clearUnreadUI(chatId) {
        VCS.state.chatUnreadCounts[chatId] = 0;
        this._updateChatUnreadBadge();

        const listItem = document.querySelector(`.chat-list-item[data-chat-id="${chatId}"]`);
        if (listItem) {
            const badge = listItem.querySelector('.chat-unread-badge');
            if (badge) badge.remove();
        }
    }

    // OS-Notifications fuer einen Chat per Service Worker schliessen
    _dismissNotifications(chatId) {
        if (!navigator.serviceWorker?.ready) return;
        navigator.serviceWorker.ready.then(reg => {
            if (!reg.getNotifications) return;
            reg.getNotifications({ tag: 'chat-' + chatId }).then(notifications => {
                notifications.forEach(n => n.close());
            }).catch(() => {});
        }).catch(() => {});
    }

    async sendText() {
        if (VCS.state.chatEditingMessageId) {
            await this.saveEdit();
            return;
        }

        const input = $('#chatTextInput')[0];
        const text = input.value.trim();
        if (!text || !VCS.state.chatActiveChatId) {
            return;
        }
        input.value = '';
        input.style.height = 'auto';
        this.clearDraft(VCS.state.chatActiveChatId);
        this.renderChatList();
        $('#chatSendBtn')[0]?.classList.add('hidden');
        $('#chatMicBtn')[0]?.classList.remove('hidden');
        this.closeEmojiPicker();

        const replyToId = VCS.state.chatReplyToId;
        const replyTo = VCS.state.chatReplyToMsg ? {
            id          : VCS.state.chatReplyToMsg.id,
            senderPhone : VCS.state.chatReplyToMsg.senderPhone,
            originalText: VCS.state.chatReplyToMsg.originalText
        } : null;
        this.clearReply();

        const tempMsg = {
            id                : 'temp_' + Date.now(),
            chatId            : VCS.state.chatActiveChatId,
            senderPhone       : VCS.state.chatMyPhone,
            originalText      : text,
            translatedText    : null,
            senderLang        : VCS.state.currentLang,
            timestamp         : Date.now(),
            status            : 'sent',
            audioUrl          : null,
            audioDurationMs   : null,
            translatedAudioUrl: null,
            replyToId         : replyToId,
            replyTo           : replyTo,
            _sending          : true
        };

        if (!VCS.state.chatMessages[VCS.state.chatActiveChatId]) {
            VCS.state.chatMessages[VCS.state.chatActiveChatId] = [];
        }
        VCS.state.chatMessages[VCS.state.chatActiveChatId].push(tempMsg);
        this.renderChatMessages();
        this.scrollToBottom();

        try {
            let sendText = text;
            let encrypted = false;
            let iv = null;
            let keyVersion = 1;
            let recipientKeyVersion = 1;

            if (VCS.state.chatE2EEnabled[VCS.state.chatActiveChatId] && typeof E2E !== 'undefined') {
                const sharedKey = await VCS.e2e?.getOrDeriveKey?.(VCS.state.chatActiveChatId);
                if (sharedKey) {
                    const enc = await E2E.encryptText(sharedKey, text);
                    sendText = enc.ciphertext;
                    iv = enc.iv;
                    encrypted = true;
                    if (sharedKey._versionInfo) {
                        keyVersion = sharedKey._versionInfo.senderVersion;
                        recipientKeyVersion = sharedKey._versionInfo.recipientVersion;
                    }
                }
            }

            const res = await fetch(VCS.Chat.API_BASE + '/messages', {
                method : 'POST',
                headers: VCS.chat?.authHeaders({'Content-Type': 'application/json'}),
                body   : JSON.stringify({
                    chatId              : VCS.state.chatActiveChatId,
                    text                : sendText,
                    senderLang          : VCS.state.currentLang,
                    translate           : encrypted ? false : (VCS.Chat.isBotChat(VCS.state.chatActiveChatId) ? false : !VCS.state.chatE2EEnabled[VCS.state.chatActiveChatId]),
                    replyToId           : replyToId || undefined,
                    encrypted           : encrypted,
                    iv                  : iv,
                    keyVersion          : keyVersion,
                    recipientKeyVersion : recipientKeyVersion
                })
            });

            if (res.ok) {
                const sent = await res.json();
                if (sent.encrypted) {
                    sent._decryptedText = text;
                }
                const msgs = VCS.state.chatMessages[VCS.state.chatActiveChatId];
                if (msgs) {
                    const tempIdx = msgs.findIndex(m => m.id === tempMsg.id);
                    if (tempIdx !== -1) {
                        msgs.splice(tempIdx, 1);
                    }
                    if (!msgs.some(m => m.id === sent.id)) {
                        this.applyPendingStatus(sent);
                        msgs.push(sent);
                    }
                    this.renderChatMessages();
                    this.scrollToBottom();
                }
                // Nicht hart auf 'sent' zuruecksetzen: bis hierher kann laengst ein
                // Zustell- oder Lese-Ack angekommen sein.
                const curStatus = msgs?.find(m => m.id === sent.id)?.status || 'sent';
                this.updateChatPreview(VCS.state.chatActiveChatId, text, Date.now(), VCS.state.chatMyPhone, curStatus);
            }
        } catch (e) {
            console.error('[chat] sendText error:', e);
        }
    }

    // Verschickt einen Sticker als eigene Nachricht (Token "sticker:NN"). Sticker
    // sind kein sensibler Inhalt und werden auch in E2E-Chats unverschluesselt
    // gesendet, damit die Gegenseite sie ohne Schluessel rendern kann.
    sendSticker(stickerId) {
        if (!stickerId) {
            return;
        }
        return this._sendTokenMessage('sticker:' + stickerId, true);
    }

    // Verschickt ein GIF als Token "gif:N" -- gleicher Weg wie Sticker, ohne Pop.
    sendGif(gifId) {
        if (!gifId) {
            return;
        }
        return this._sendTokenMessage('gif:' + gifId, false);
    }

    async _sendTokenMessage(token, popSticker) {
        const chatId = VCS.state.chatActiveChatId;
        if (!chatId) {
            return;
        }

        const replyToId = VCS.state.chatReplyToId;
        const replyTo = VCS.state.chatReplyToMsg ? {
            id          : VCS.state.chatReplyToMsg.id,
            senderPhone : VCS.state.chatReplyToMsg.senderPhone,
            originalText: VCS.state.chatReplyToMsg.originalText
        } : null;
        this.clearReply();

        const tempMsg = {
            id                : 'temp_' + Date.now(),
            chatId            : chatId,
            senderPhone       : VCS.state.chatMyPhone,
            originalText      : token,
            translatedText    : null,
            senderLang        : VCS.state.currentLang,
            timestamp         : Date.now(),
            status            : 'sent',
            audioUrl          : null,
            audioDurationMs   : null,
            translatedAudioUrl: null,
            replyToId         : replyToId,
            replyTo           : replyTo,
            _sending          : true
        };

        if (!VCS.state.chatMessages[chatId]) {
            VCS.state.chatMessages[chatId] = [];
        }
        VCS.state.chatMessages[chatId].push(tempMsg);
        this.renderChatMessages();
        this.scrollToBottom();
        this.renderChatList();

        try {
            const res = await fetch(VCS.Chat.API_BASE + '/messages', {
                method : 'POST',
                headers: VCS.chat?.authHeaders({'Content-Type': 'application/json'}),
                body   : JSON.stringify({
                    chatId    : chatId,
                    text      : token,
                    senderLang: VCS.state.currentLang,
                    translate : false,
                    replyToId : replyToId || undefined,
                    encrypted : false
                })
            });

            if (res.ok) {
                const sent = await res.json();
                const msgs = VCS.state.chatMessages[chatId];
                if (msgs) {
                    const tempIdx = msgs.findIndex(m => m.id === tempMsg.id);
                    if (tempIdx !== -1) {
                        msgs.splice(tempIdx, 1);
                    }
                    if (!msgs.some(m => m.id === sent.id)) {
                        this.applyPendingStatus(sent);
                        msgs.push(sent);
                    }
                    // Eigenen, gerade gesendeten Sticker einmalig poppen lassen.
                    if (popSticker) {
                        this._stickerPopPending.add(sent.id);
                    }
                    this.renderChatMessages();
                    this.scrollToBottom();
                }
                const curStatus = VCS.state.chatMessages[chatId]?.find(m => m.id === sent.id)?.status || 'sent';
                this.updateChatPreview(chatId, token, Date.now(), VCS.state.chatMyPhone, curStatus);
            } else {
                console.error('[chat] sendTokenMessage fehlgeschlagen:', token, res.status);
            }
        } catch (e) {
            console.error('[chat] sendTokenMessage error:', token, e);
        }
    }

    // --- Nachrichten-Entwuerfe (Drafts) pro Chat in localStorage ---
    // Haelt getippten, noch nicht gesendeten Text fest, damit er einen
    // Verbindungsabbruch oder Neustart der App uebersteht.
    _draftKey(chatId) {
        return 'vcs_draft_' + chatId;
    }

    // Speichert den aktuellen Eingabetext fuer den aktiven Chat (bei jedem Tippen).
    saveDraft() {
        // Beim Bearbeiten einer bestehenden Nachricht keinen Draft schreiben.
        if (VCS.state.chatEditingMessageId) {
            return;
        }
        const chatId = VCS.state.chatActiveChatId;
        const input = $('#chatTextInput')[0];
        if (!chatId || !input) {
            return;
        }
        const hasText = input.value.trim().length > 0;
        let hadDraft = false;
        try {
            hadDraft = !!localStorage.getItem(this._draftKey(chatId));
            if (hasText) {
                localStorage.setItem(this._draftKey(chatId), input.value);
            } else {
                localStorage.removeItem(this._draftKey(chatId));
            }
        } catch (e) {
            // localStorage kann voll/gesperrt sein -- Draft ist nicht kritisch
        }
        // Nur beim Uebergang leer<->Text die Liste neu zeichnen (Entwurf-Label)
        if (hadDraft !== hasText) {
            this.renderChatList();
        }
    }

    // Loescht den Entwurf eines Chats (z.B. nachdem die Nachricht gesendet wurde).
    clearDraft(chatId) {
        if (!chatId) {
            return;
        }
        try {
            localStorage.removeItem(this._draftKey(chatId));
        } catch (e) {}
    }

    // Laedt den gespeicherten Entwurf in das Eingabefeld und stellt die UI her:
    // Hoehe anpassen, Send-/Mic-Button umschalten, Cursor ans Ende, ans Ende scrollen.
    restoreDraft(chatId) {
        const input = $('#chatTextInput')[0];
        if (!input) {
            return;
        }
        let text = '';
        try {
            text = localStorage.getItem(this._draftKey(chatId)) || '';
        } catch (e) {
            text = '';
        }
        input.value = text;

        // Auto-Resize (gleiche Logik wie der input-Handler)
        input.style.height = 'auto';
        input.style.height = input.scrollHeight + 'px';
        input.style.overflowY = input.scrollHeight > 120 ? 'auto' : 'hidden';

        // Send-/Mic-Button passend zum Inhalt umschalten
        const hasText = text.trim().length > 0;
        $('#chatSendBtn')[0]?.classList.toggle('hidden', !hasText);
        $('#chatMicBtn')[0]?.classList.toggle('hidden', hasText);

        // Cursor ans Textende setzen und zur letzten Zeile scrollen
        if (hasText) {
            const len = input.value.length;
            try {
                input.setSelectionRange(len, len);
            } catch (e) {}
            input.scrollTop = input.scrollHeight;
        }
    }

    sendTyping() {
        const now = Date.now();
        if (now - VCS.state.chatLastTypingSent < 3000) {
            return;
        }
        VCS.state.chatLastTypingSent = now;
        VCS.chat?.wsSend?.({
            type  : 'chat-typing',
            chatId: VCS.state.chatActiveChatId,
            phone : VCS.state.chatMyPhone
        });
    }

    updateScrollFab() {
        const container = $('#chatMessages')[0];
        const fab = $('#chatScrollFab')[0];
        if (!container || !fab) {
            return;
        }
        const atBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
        fab.classList.toggle('hidden', atBottom);
        if (atBottom) {
            VCS.state.chatScrollFabNewCount = 0;
            $('#chatScrollFabCount')[0]?.classList.add('hidden');
        }
    }

    scrollFabClick() {
        this.scrollToBottom();
        VCS.state.chatScrollFabNewCount = 0;
        const count = $('#chatScrollFabCount')[0];
        if (count) {
            count.classList.add('hidden');
            count.textContent = '0';
        }
        $('#chatScrollFab')[0]?.classList.add('hidden');
    }

    incrementScrollFab() {
        const container = $('#chatMessages')[0];
        if (!container) {
            return;
        }
        const atBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
        if (atBottom) {
            return;
        }
        VCS.state.chatScrollFabNewCount++;
        const count = $('#chatScrollFabCount')[0];
        if (count) {
            count.textContent = String(VCS.state.chatScrollFabNewCount);
            count.classList.remove('hidden');
        }
    }

    bindBubbleEvents(container) {
        container.querySelectorAll('.chat-bubble-reply').forEach(el => {
            el.onclick = () => {
                const targetId = el.dataset.scrollTo;
                if (!targetId) {
                    return;
                }
                const target = container.querySelector(`[data-msg-id="${targetId}"]`);
                if (target) {
                    target.scrollIntoView({behavior: 'smooth', block: 'center'});
                    target.style.transition = 'background 0.3s';
                    target.style.background = 'rgba(96, 165, 250, 0.15)';
                    setTimeout(() => {
                        target.style.background = '';
                    }, 1500);
                }
            };
        });

        // Container-Level-Listener nur einmalig binden — sonst akkumulieren sie
        // bei jedem Re-Render und Events feuern N-fach (Toast 3x, playVideo 3x, ...).
        if (!container._vcsBubbleEventsBound) {
            container._vcsBubbleEventsBound = true;

            // Zentraler data-action-Dispatcher (ersetzt frueher inline onclick="..." in Templates).
            container.addEventListener('click', (e) => {
                const el = e.target.closest('[data-action]');
                if (!el || !container.contains(el)) {
                    return;
                }
                const a = el.dataset.action;
                const id = el.dataset.id || '';
                switch (a) {
                    case 'lightbox':
                        VCS.chatMedia?.openLightbox?.(el.dataset.src);
                        break;
                    case 'toggle-vp-hint':
                        el.closest('.chat-voicephoto')?.querySelector('.chat-voicephoto-play')?.click();
                        break;
                    case 'play-voicephoto':
                        VCS.chatVoice?.playVoicephoto?.(el, id);
                        break;
                    case 'seek-voicephoto':
                        VCS.chatVoice?.seekVoicephoto?.(e, id);
                        break;
                    case 'video-variant-orig':
                        VCS.chatVideo?.setVideoVariant?.(id, false);
                        break;
                    case 'video-variant-trans':
                        VCS.chatVideo?.setVideoVariant?.(id, true);
                        break;
                    case 'play-video':
                        // revokeBlobOnDetach=false: Blob-URL gehoert mediaCache bzw. der Message
                        VCS.chatVideo?.playVideo?.(el, el.dataset.src, el.dataset.karaoke || '', true, null, null, false);
                        break;
                    case 'save-video':
                        VCS.chatVideo?.saveVideoMessage?.(id);
                        break;
                    case 'save-document': {
                        // Dokument herunterladen (entschluesselt generischen Blob, octet-stream-Wrapper)
                        const docMsg = (VCS.state.chatMessages[VCS.state.chatActiveChatId] || []).find(m => m.id === id);
                        if (docMsg) {
                            this.downloadMessageMedia(docMsg);
                        }
                        break;
                    }
                    case 'view-document': {
                        // Klick auf die Kachel zeigt das PDF an. Der
                        // Download-Button liegt darin und gewinnt via closest().
                        const viewMsg = (VCS.state.chatMessages[VCS.state.chatActiveChatId] || []).find(m => m.id === id);
                        if (viewMsg) {
                            VCS.chatMedia?.openPdfViewer?.(viewMsg);
                        }
                        break;
                    }
                    case 'play-audio':
                        VCS.chatVoice?.playAudio?.(id, el.dataset.variant);
                        break;
                    case 'music-speed':
                        VCS.chatVoice?.cycleMusicSpeed?.(id);
                        break;
                }
            });

            // Status-Info Popup bei Klick/Tap (WhatsApp-Style, Event-Delegation)
            container.addEventListener('click', (e) => {
                const statusEl = e.target.closest('[data-status]');
                if (statusEl) {
                    e.stopPropagation();
                    this._showStatusPopup(statusEl);
                }
            });
        }

        let touchData = null;
        let longPressTimer = null;
        const SWIPE_THRESHOLD = 60;
        const SWIPE_MAX = 80;
        const LONG_PRESS_MS = 400;

        container.querySelectorAll('.chat-bubble[data-msg-id]').forEach(bubble => {
            bubble.addEventListener('contextmenu', (e) => {
                e.preventDefault();
                this.showContextMenu(e.clientX, e.clientY, bubble.dataset.msgId);
            });
            bubble.addEventListener('touchstart', (e) => {
                // Touches innerhalb des Custom-Video-Players: Swipe-to-Reply blocken,
                // Longpress fuer Kontext-Menue laeuft trotzdem.
                const inVideoPlayer = !!e.target.closest('.chat-video-player-wrap');
                const touch = e.touches[0];
                touchData = {
                    x: touch.clientX, y: touch.clientY,
                    id: bubble.dataset.msgId,
                    target: e.target,
                    swiping: false, locked: inVideoPlayer, fired: false
                };
                longPressTimer = setTimeout(() => {
                    if (!touchData || touchData.swiping || touchData.fired) return;
                    touchData.fired = true;
                    if (!e.target.closest('a, button, .chat-reaction-badge, .chat-vp-scrubber')) {
                        this.showContextMenu(touchData.x, touchData.y, touchData.id);
                    }
                }, LONG_PRESS_MS);
            }, {passive: true});
            bubble.addEventListener('touchmove', (e) => {
                if (!touchData || touchData.fired) return;
                const touch = e.touches[0];
                const dx = touch.clientX - touchData.x;
                const dy = Math.abs(touch.clientY - touchData.y);

                // Bei >10px Bewegung Longpress canceln (auch im Player beim Scrubben).
                if (longPressTimer && (Math.abs(dx) > 10 || dy > 10)) {
                    clearTimeout(longPressTimer);
                    longPressTimer = null;
                }
                if (touchData.locked) return;

                // Entscheide: Swipe oder Scroll
                if (!touchData.swiping && (Math.abs(dx) > 10 || dy > 10)) {
                    if (dx > 10 && dy < 20) {
                        touchData.swiping = true;
                    } else {
                        touchData.locked = true;
                        return;
                    }
                }

                if (touchData.swiping && dx > 0) {
                    e.preventDefault();
                    const offset = Math.min(dx, SWIPE_MAX);
                    bubble.style.transform = `translateX(${offset}px)`;
                    bubble.style.transition = 'none';
                    const icon = bubble.querySelector('.chat-swipe-reply-icon');
                    if (icon) {
                        icon.style.opacity = Math.min(offset / SWIPE_THRESHOLD, 1);
                        icon.style.transform = `scale(${Math.min(offset / SWIPE_THRESHOLD, 1)})`;
                    }
                }
            }, {passive: false});
            bubble.addEventListener('touchend', () => {
                clearTimeout(longPressTimer);
                if (!touchData) return;

                if (touchData.swiping) {
                    const currentX = parseFloat(bubble.style.transform.replace(/[^0-9.-]/g, '')) || 0;
                    bubble.style.transition = 'transform 0.2s ease';
                    bubble.style.transform = '';
                    const icon = bubble.querySelector('.chat-swipe-reply-icon');
                    if (icon) {
                        icon.style.opacity = '';
                        icon.style.transform = '';
                    }
                    if (currentX >= SWIPE_THRESHOLD && !VCS.Chat.isBotChat(VCS.state.chatActiveChatId)) {
                        const msgs = VCS.state.chatMessages[VCS.state.chatActiveChatId] || [];
                        const msg = msgs.find(m => m.id === touchData.id);
                        if (msg) this.setReply(msg);
                    }
                } else if (!touchData.fired) {
                    // Kurzer Tap auf Status-Icon: Popup anzeigen
                    const statusEl = touchData.target?.closest('[data-status]');
                    if (statusEl) {
                        this._showStatusPopup(statusEl);
                    }
                }
                touchData = null;
            });
        });

        container.querySelectorAll('.chat-reaction-badge').forEach(badge => {
            badge.addEventListener('click', () => {
                const emoji = badge.dataset.emoji;
                const msgId = badge.dataset.msgId;
                if (emoji && msgId && VCS.state.chatActiveChatId) {
                    fetch(VCS.Chat.API_BASE + '/messages/react', {
                        method : 'POST',
                        headers: VCS.chat?.authHeaders({'Content-Type': 'application/json'}),
                        body   : JSON.stringify({
                            messageId: msgId,
                            chatId   : VCS.state.chatActiveChatId,
                            emoji
                        })
                    }).catch(e => console.error('[chat] badge toggle error:', e));
                }
            });
        });

        container.querySelectorAll('.chat-music-wave').forEach(wave => {
            VCS.chatVoice?.bindMusicWave?.(wave);
        });

        container.querySelectorAll('.chat-voice-seek').forEach(slider => {
            const key = slider.dataset.voiceSeek;
            if (!key) {
                return;
            }
            const sep = key.lastIndexOf('-');
            const messageId = key.substring(0, sep);
            const type = key.substring(sep + 1);

            VCS.chatVoice?.updateSeekSliderGradient?.(slider);

            slider.addEventListener('input', () => {
                slider._userSeeking = true;
                VCS.chatVoice?.updateSeekSliderGradient?.(slider);
                VCS.chatVoice?.seekAudio?.(messageId, type, parseInt(slider.value, 10));
            });
            slider.addEventListener('change', () => {
                slider._userSeeking = false;
                VCS.chatVoice?.seekAudio?.(messageId, type, parseInt(slider.value, 10));
            });
        });
    }

    showContextMenu(x, y, msgId) {
        // Bot-Chat: kein Context-Menu (keine Reaktionen, Reply, Edit, Delete)
        if (VCS.Chat.isBotChat(VCS.state.chatActiveChatId)) return;

        this.hideContextMenu();

        const wrap = $('#chatContextMenuWrap')[0];
        const menu = $('#chatContextMenu')[0];
        const overlay = $('#chatContextOverlay')[0];
        if (!wrap || !menu) return;

        const msgs = VCS.state.chatMessages[VCS.state.chatActiveChatId] || [];
        const msg = msgs.find(m => m.id === msgId);
        if (!msg) return;

        const isMine = msg.senderPhone === VCS.state.chatMyPhone;
        const hasText = !!msg.originalText;
        const isPending = msg.mediaUrl === 'pending';
        const hasDownloadable = !isPending && (!!msg.audioUrl || (!!msg.mediaUrl && msg.mediaUrl !== 'pending'));
        menu.querySelector('[data-action="copy"]').classList.toggle('hidden', !hasText);
        menu.querySelector('[data-action="forward"]').classList.toggle('hidden', isPending);
        menu.querySelector('[data-action="edit"]').classList.toggle('hidden', !isMine || !!msg.audioUrl || !!msg.mediaUrl);
        menu.querySelector('[data-action="download"]').classList.toggle('hidden', !hasDownloadable);
        menu.querySelector('[data-action="share"]').classList.toggle('hidden', !hasDownloadable || typeof navigator.share !== 'function');
        menu.querySelector('[data-action="delete"]').classList.remove('hidden');

        const bar = $('#chatReactionBar')[0];
        if (bar) {
            const recent = this._getRecentEmojis();
            // Nur die Emoji-Buttons sortieren; der Mehr-Button bleibt immer rechts.
            const moreBtn = bar.querySelector('.chat-reaction-more');
            const buttons = [...bar.querySelectorAll('.chat-reaction-btn')].filter(b => b.dataset.emoji);
            buttons.sort((a, b) => {
                const ia = recent.indexOf(a.dataset.emoji);
                const ib = recent.indexOf(b.dataset.emoji);
                if (ia !== -1 && ib !== -1) return ia - ib;
                if (ia !== -1) return -1;
                if (ib !== -1) return 1;
                return 0;
            });
            buttons.forEach(btn => bar.appendChild(btn));
            if (moreBtn) bar.appendChild(moreBtn);
            buttons.forEach(btn => {
                const emoji = btn.dataset.emoji;
                const reactors = msg.reactions?.[emoji] || [];
                btn.classList.toggle('chat-reaction-btn--active', reactors.includes(VCS.state.chatMyPhone));
            });
        }

        // Bubble finden und hervorheben (ueber Overlay sichtbar)
        const bubble = document.querySelector(`[data-msg-id="${msgId}"]`);
        if (!bubble) return;
        bubble.classList.add('chat-bubble--highlighted');

        // Overlay abdunkeln
        if (overlay) overlay.classList.remove('hidden');

        // i18n vor dem Messen aufloesen, damit Menue-Breite stimmt
        menu.querySelectorAll('[data-i18n]').forEach(el => {
            el.textContent = t(el.dataset.i18n);
        });

        // Telegram-Style: Bar UEBER, Menue UNTER dem Klickpunkt
        // Wrap zunaechst offscreen einblenden, um Groessen zu messen
        wrap.classList.remove('chat-context-wrap--flipped');
        wrap.style.left = '-9999px';
        wrap.style.top = '-9999px';
        wrap.classList.remove('hidden');

        const barH = bar ? bar.offsetHeight : 0;
        const menuH = menu ? menu.offsetHeight : 0;
        const wrapW = wrap.offsetWidth;
        const gap = 6;
        const pad = 8;

        // Sichtbare Grenzen = Chat-Container (Header + Eingabe ausgespart)
        const container = $('#chatMessages')[0];
        const containerRect = container ? container.getBoundingClientRect() : {
            top: 0, bottom: window.innerHeight, left: 0, right: window.innerWidth
        };
        const minTop = containerRect.top + pad;
        const maxBottom = containerRect.bottom - pad;

        // Klickpunkt auf Container-Bereich klemmen
        const cx = Math.max(containerRect.left + pad, Math.min(x, containerRect.right - pad));
        const cy = Math.max(minTop, Math.min(y, maxBottom));

        // Orientierung waehlen: Default = Bar oben, Menue unten
        // Flip wenn unten kein Platz fuers Menue UND oben mehr Platz ist
        const spaceAbove = cy - minTop;
        const spaceBelow = maxBottom - cy;
        let flipped = false;
        if (spaceBelow < (gap + menuH) && spaceAbove > spaceBelow) {
            flipped = true;
        }

        let barTop, menuTop;
        if (!flipped) {
            barTop = cy - barH - gap;
            menuTop = cy + gap;
        } else {
            menuTop = cy - menuH - gap;
            barTop = cy + gap;
        }

        // Block (Bar + Gap + Menue) als Einheit am Container-Rand klemmen
        const blockTop = Math.min(barTop, menuTop);
        const blockBottom = Math.max(barTop + barH, menuTop + menuH);
        let shift = 0;
        if (blockTop < minTop) {
            shift = minTop - blockTop;
        } else if (blockBottom > maxBottom) {
            shift = maxBottom - blockBottom;
        }
        barTop += shift;
        menuTop += shift;

        const wrapTop = Math.min(barTop, menuTop);
        // Reihenfolge im DOM via flipped-Klasse umkehren
        if (flipped) wrap.classList.add('chat-context-wrap--flipped');

        // Horizontal: am Klickpunkt zentrieren, an Container-Raendern klemmen
        let left = cx - wrapW / 2;
        left = Math.max(containerRect.left + pad, Math.min(left, containerRect.right - wrapW - pad));

        wrap.style.left = left + 'px';
        wrap.style.top = wrapTop + 'px';
        wrap._msgId = msgId;
    }

    _showStatusPopup(el) {
        // Altes Popup entfernen
        document.querySelector('.chat-status-popup')?.remove();

        const status = el.dataset.status;
        const ts = parseInt(el.dataset.ts, 10);
        const labels = { sent: t('statusSent'), delivered: t('statusDelivered'), read: t('statusRead') };
        const icons = {
            sent: '<span style="color:#ef4444">&#10003;</span>',
            delivered: '<span style="color:#f59e0b">&#10003;&#10003;</span>',
            read: '<span style="color:#22c55e">&#10003;&#10003;&#10003;</span>'
        };
        const label = labels[status] || status;
        const fmt = (ts) => ts ? VCS.chat?.formatTime?.(ts) || '' : '';
        const deliveredAt = el.dataset.deliveredAt ? parseInt(el.dataset.deliveredAt, 10) : 0;
        const readAt = el.dataset.readAt ? parseInt(el.dataset.readAt, 10) : 0;

        let lines = '';
        if (status === 'sent') {
            lines = `${icons.sent} ${escapeHtml(label)}`;
        } else if (status === 'delivered') {
            lines = `${icons.delivered} ${escapeHtml(label)}`;
            if (deliveredAt) lines += ` · ${escapeHtml(fmt(deliveredAt))}`;
        } else if (status === 'read') {
            if (deliveredAt) {
                lines += `${icons.delivered} ${escapeHtml(t('statusDelivered'))} · ${escapeHtml(fmt(deliveredAt))}<br>`;
            }
            lines += `${icons.read} ${escapeHtml(label)}`;
            if (readAt) lines += ` · ${escapeHtml(fmt(readAt))}`;
        }

        const popup = document.createElement('div');
        popup.className = 'chat-status-popup';
        popup.innerHTML = lines;

        // Fixe Positionierung direkt ueber den Haken
        document.body.appendChild(popup);
        const rect = el.getBoundingClientRect();
        popup.style.left = Math.max(8, rect.left - popup.offsetWidth / 2 + rect.width / 2) + 'px';
        popup.style.top = (rect.top - popup.offsetHeight - 6) + 'px';

        // Auto-Close nach 3s oder bei Klick/Tap irgendwo
        const close = () => {
            popup.remove();
            document.removeEventListener('pointerdown', close);
        };
        setTimeout(() => document.addEventListener('pointerdown', close), 10);
        setTimeout(close, 3000);
    }

    hideContextMenu() {
        this._closeReactionEmoji();
        const wrap = $('#chatContextMenuWrap')[0];
        wrap?.classList.add('hidden');
        wrap?.classList.remove('chat-context-wrap--flipped');
        $('#chatContextOverlay')[0]?.classList.add('hidden');

        // Bubble-Highlight zuruecksetzen
        const highlighted = document.querySelector('.chat-bubble--highlighted');
        if (highlighted) {
            highlighted.classList.remove('chat-bubble--highlighted');
        }
    }

    _getRecentEmojis() {
        try {
            return JSON.parse(localStorage.getItem('vcs_recent_emojis') || '[]');
        } catch (_) { return []; }
    }

    _saveRecentEmoji(emoji) {
        const recent = this._getRecentEmojis().filter(e => e !== emoji);
        recent.unshift(emoji);
        if (recent.length > 8) recent.length = 8;
        localStorage.setItem('vcs_recent_emojis', JSON.stringify(recent));
    }

    _animateReactionFly(emoji, btnEl, msgId) {
        const bubble = document.querySelector(`[data-msg-id="${msgId}"]`);
        if (!btnEl || !bubble) return;

        const btnRect = btnEl.getBoundingClientRect();
        const bubbleRect = bubble.getBoundingClientRect();

        // Zielposition: unter der Bubble, mittig
        const targetX = bubbleRect.left + bubbleRect.width / 2;
        const targetY = bubbleRect.bottom;

        // Startposition: Mitte des Buttons
        const startX = btnRect.left + btnRect.width / 2;
        const startY = btnRect.top + btnRect.height / 2;

        // Grosses Pop-Emoji am Button
        const pop = document.createElement('div');
        pop.textContent = emoji;
        pop.className = 'chat-reaction-fly-pop';
        pop.style.left = startX + 'px';
        pop.style.top = startY + 'px';
        document.body.appendChild(pop);
        pop.addEventListener('animationend', () => pop.remove());

        // Fliegendes Emoji von Button zur Bubble
        const fly = document.createElement('div');
        fly.textContent = emoji;
        fly.className = 'chat-reaction-fly';
        fly.style.left = startX + 'px';
        fly.style.top = startY + 'px';
        fly.style.setProperty('--fly-dx', (targetX - startX) + 'px');
        fly.style.setProperty('--fly-dy', (targetY - startY) + 'px');
        document.body.appendChild(fly);
        fly.addEventListener('animationend', () => fly.remove());
    }

    async toggleReaction(emoji) {
        const wrap = $('#chatContextMenuWrap')[0];
        const msgId = wrap?._msgId;
        if (!msgId || !VCS.state.chatActiveChatId) {
            return;
        }

        // Animation starten bevor Context-Menu geschlossen wird
        const btnEl = wrap.querySelector(`.chat-reaction-btn[data-emoji="${CSS.escape(emoji)}"]`);
        // Fly-Animation und Recent-Liste nur fuer echte Emojis, nicht fuer Sticker-Token.
        if (!this._stickerIdFromText(emoji)) {
            this._animateReactionFly(emoji, btnEl, msgId);
            this._saveRecentEmoji(emoji);
        }
        this.hideContextMenu();
        try {
            await fetch(VCS.Chat.API_BASE + '/messages/react', {
                method : 'POST',
                headers: VCS.chat?.authHeaders({'Content-Type': 'application/json'}),
                body   : JSON.stringify({
                    messageId: msgId,
                    chatId   : VCS.state.chatActiveChatId,
                    emoji
                })
            });
        } catch (e) {
            console.error('[chat] toggleReaction error:', e);
        }
    }

    // Oeffnet den schwebenden Emoji-Picker fuer beliebige Reactions. Positioniert
    // ihn ueber der Reaction-Leiste; die Auswahl loest toggleReaction() aus.
    openReactionEmojiPicker() {
        const wrap = $('#reactionEmojiWrap')[0];
        const bar  = $('#chatReactionBar')[0];
        if (!wrap || !bar) {
            return;
        }
        // Gemeinsame Picker-Factory; bei Auswahl direkt reagieren.
        // GIFs sind keine Reaktion -> nur Emoji + Sticker.
        const picker = this.createEmojiPickerElement((emoji) => this.toggleReaction(emoji), { gifs: false });
        wrap.innerHTML = '';
        wrap.appendChild(picker);
        // Erst sichtbar machen (fuer Groessenmessung), dann positionieren.
        wrap.classList.remove('hidden');

        const barRect = bar.getBoundingClientRect();
        const ww  = wrap.offsetWidth;
        const wh  = wrap.offsetHeight;
        const pad = 8;
        // Horizontal an der Leiste zentrieren, am Viewport klemmen.
        let left = barRect.left + barRect.width / 2 - ww / 2;
        left = Math.max(pad, Math.min(left, window.innerWidth - ww - pad));
        // Bevorzugt ueber der Leiste; bei zu wenig Platz darunter.
        let top = barRect.top - wh - 6;
        if (top < pad) {
            top = barRect.bottom + 6;
        }
        top = Math.max(pad, Math.min(top, window.innerHeight - wh - pad));
        wrap.style.left = left + 'px';
        wrap.style.top  = top + 'px';
    }

    _closeReactionEmoji() {
        const wrap = $('#reactionEmojiWrap')[0];
        if (wrap) {
            wrap.classList.add('hidden');
            wrap.innerHTML = '';
        }
    }

    handleContextAction(action) {
        const wrap = $('#chatContextMenuWrap')[0];
        const msgId = wrap?._msgId;
        if (!msgId) {
            return;
        }
        this.hideContextMenu();

        const msgs = VCS.state.chatMessages[VCS.state.chatActiveChatId] || [];
        const msg = msgs.find(m => m.id === msgId);
        if (!msg) {
            return;
        }

        switch (action) {
            case 'reply':
                this.setReply(msg);
                break;
            case 'forward':
                VCS.chatForward?.open?.(msg);
                break;
            case 'copy': {
                let copyText = msg.originalText || '';
                if (msg.translatedText) {
                    copyText += '\n\n' + t('chatAutoTranslated') + ':\n' + msg.translatedText;
                }
                if (copyText) {
                    navigator.clipboard.writeText(copyText).then(() => {
                        VCS.ui?.showToast?.(t('chatCopied'), 'info');
                    }).catch(() => {});
                }
                break;
            }
                break;
            case 'edit':
                this.startEdit(msg);
                break;
            case 'download':
                this.downloadMessageMedia(msg);
                break;
            case 'share':
                this.shareMessageMedia(msg);
                break;
            case 'delete':
                this.deleteMessage(msg);
                break;
        }
    }

    async _fetchMessageMediaForExport(msg) {
        const url = msg.mediaUrl && msg.mediaUrl !== 'pending' ? msg.mediaUrl : msg.audioUrl;
        if (!url) {
            console.warn('[chat] export skipped, no media url for msg', msg.id);
            return null;
        }

        const isAudio   = !msg.mediaUrl || msg.mediaUrl === 'pending';
        const mediaType = msg.mediaType || (isAudio ? 'voice' : 'file');
        const mimeMap = {
            image     : 'image/jpeg',
            voicephoto: 'image/jpeg',
            video     : 'video/mp4',
            music     : 'audio/mpeg',
            voice     : 'audio/mp4',
            document  : 'application/pdf'
        };
        const mimeType = mimeMap[mediaType] || 'application/octet-stream';

        // Original-Filename aus Upload bevorzugen, sonst generierter Name
        let filename;
        const orig = !isAudio && msg.mediaFileName && msg.mediaFileName.trim();
        if (orig) {
            filename = msg.mediaFileName.trim();
        } else {
            const extMap   = { image: 'jpg', voicephoto: 'jpg', video: 'mp4', music: 'mp3', voice: 'm4a', document: 'pdf' };
            const slugMap  = { image: 'foto', voicephoto: 'foto', video: 'video', music: 'audio', voice: 'voice', document: 'dokument' };
            const ext      = extMap[mediaType] || 'bin';
            const slug     = slugMap[mediaType] || 'media';
            const ts       = new Date();
            const pad      = n => String(n).padStart(2, '0');
            const stamp    = `${ts.getFullYear()}${pad(ts.getMonth() + 1)}${pad(ts.getDate())}-${pad(ts.getHours())}${pad(ts.getMinutes())}${pad(ts.getSeconds())}`;
            filename = `bernoria-${slug}-${stamp}.${ext}`;
        }

        try {
            let blob;
            if (isAudio) {
                const resp = await fetch(url);
                if (!resp.ok) {
                    console.error('[chat] export: fetch failed', resp.status, msg.id);
                    VCS.ui?.showToast?.(t('chatDownloadFailed'), 'error');
                    return null;
                }
                const fetched = await resp.blob();
                if (msg.encrypted && msg.iv && typeof E2E !== 'undefined' && VCS.e2e) {
                    const vInfo     = VCS.e2e._buildVersionInfo(msg);
                    const sharedKey = await VCS.e2e.getOrDeriveKey(msg.chatId, vInfo);
                    if (!sharedKey) {
                        console.error('[chat] export: missing key for', msg.id);
                        VCS.ui?.showToast?.(t('chatDownloadFailed'), 'error');
                        return null;
                    }
                    const decBlob = await E2E.decryptBlob(sharedKey, fetched, msg.iv, 'audio/mp4');
                    if (!decBlob) {
                        console.error('[chat] export: decrypt failed', msg.id);
                        VCS.ui?.showToast?.(t('chatDownloadFailed'), 'error');
                        return null;
                    }
                    blob = decBlob;
                } else {
                    blob = fetched;
                }
            } else {
                const srcUrl = (await VCS.e2e?.getDecryptedMediaUrl?.(msg)) || url;
                const resp   = await fetch(srcUrl);
                if (!resp.ok) {
                    console.error('[chat] export: fetch failed', resp.status, msg.id);
                    VCS.ui?.showToast?.(t('chatDownloadFailed'), 'error');
                    return null;
                }
                blob = await resp.blob();
            }

            return { blob, filename, mimeType };
        } catch (e) {
            console.error('[chat] export error', msg.id, e);
            VCS.ui?.showToast?.(t('chatDownloadFailed'), 'error');
            return null;
        }
    }

    async downloadMessageMedia(msg) {
        const data = await this._fetchMessageMediaForExport(msg);
        if (!data) return;

        // octet-stream-Wrapper erzwingt echten Download statt Tab-Preview
        const dlBlob    = new Blob([data.blob], { type: 'application/octet-stream' });
        const objectUrl = URL.createObjectURL(dlBlob);

        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = data.filename;
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        console.log('[chat] download triggered', { msgId: msg.id, filename: data.filename, size: data.blob.size });
        setTimeout(() => URL.revokeObjectURL(objectUrl), 4000);
    }

    async shareMessageMedia(msg) {
        const data = await this._fetchMessageMediaForExport(msg);
        if (!data) return;

        const file = new File([data.blob], data.filename, { type: data.mimeType });
        if (typeof navigator.share !== 'function' || !navigator.canShare || !navigator.canShare({ files: [file] })) {
            console.warn('[chat] share not supported for this file', msg.id);
            VCS.ui?.showToast?.(t('chatDownloadFailed'), 'error');
            return;
        }
        try {
            await navigator.share({ files: [file] });
            console.log('[chat] share succeeded', { msgId: msg.id, filename: data.filename, size: data.blob.size });
        } catch (e) {
            if (e && e.name === 'AbortError') {
                console.log('[chat] share aborted by user', msg.id);
                return;
            }
            console.error('[chat] share error', msg.id, e);
            VCS.ui?.showToast?.(t('chatDownloadFailed'), 'error');
        }
    }

    setReply(msg) {
        VCS.state.chatReplyToId = msg.id;
        VCS.state.chatReplyToMsg = msg;
        const peerName = msg.senderPhone === VCS.state.chatMyPhone ? (VCS.state.chatMyName || t('you')) : (this.getPeerName() || msg.senderPhone);
        $('#chatReplyName')[0].textContent = peerName;
        $('#chatReplyText')[0].textContent = msg._decryptedText || msg.originalText || t('chatVoiceMessage');
        $('#chatReplyBar')[0]?.classList.remove('hidden');
        $('#chatTextInput')[0]?.focus();
    }

    clearReply() {
        VCS.state.chatReplyToId = null;
        VCS.state.chatReplyToMsg = null;
        $('#chatReplyBar')[0]?.classList.add('hidden');
    }

    startEdit(msg) {
        VCS.state.chatEditingMessageId = msg.id;
        const input = $('#chatTextInput')[0];
        if (input) {
            input.value = msg._decryptedText || msg.originalText;
            input.focus();
        }
    }

    async saveEdit() {
        const input = $('#chatTextInput')[0];
        const newText = input?.value.trim();
        if (!newText || !VCS.state.chatEditingMessageId || !VCS.state.chatActiveChatId) {
            return;
        }
        input.value = '';
        input.style.height = 'auto';
        $('#chatSendBtn')[0]?.classList.add('hidden');
        $('#chatMicBtn')[0]?.classList.remove('hidden');

        try {
            await fetch(VCS.Chat.API_BASE + '/messages/edit', {
                method : 'POST',
                headers: VCS.chat?.authHeaders({'Content-Type': 'application/json'}),
                body   : JSON.stringify({
                    messageId: VCS.state.chatEditingMessageId,
                    chatId   : VCS.state.chatActiveChatId,
                    newText
                })
            });
        } catch (e) {
            console.error('[chat] edit error:', e);
        }
        VCS.state.chatEditingMessageId = null;
    }

    async deleteMessage(msg) {
        const isMine = msg.senderPhone === VCS.state.chatMyPhone;
        const mode = await this._showDeleteChoiceSheet(isMine);
        if (!mode) {
            return;
        }

        // Shrink + Fade Animation auf der Bubble
        const bubble = document.querySelector(`[data-msg-id="${msg.id}"]`);
        if (bubble) {
            bubble.classList.add('chat-bubble--deleting');
            const reactions = bubble.nextElementSibling;
            if (reactions?.classList.contains('chat-reactions')) {
                reactions.classList.add('chat-bubble--deleting');
            }
            await new Promise(resolve => {
                bubble.addEventListener('animationend', resolve, { once: true });
            });
        }

        // Optimistic: sofort aus lokalem Array entfernen + re-rendern
        const chatId = VCS.state.chatActiveChatId;
        const msgs = VCS.state.chatMessages[chatId];
        if (msgs) {
            const idx = msgs.findIndex(m => m.id === msg.id);
            if (idx !== -1) msgs.splice(idx, 1);
            this.renderChatMessages();
        }

        // Chat-Preview lokal aktualisieren (sofortiges Update ohne Server-Roundtrip)
        const chat = VCS.state.chatChats?.find(c => c.id === chatId);
        if (chat) {
            const remaining = VCS.state.chatMessages[chatId] || [];
            // Letzte sichtbare Message: hoechste timestamp (Array koennte ungeordnet sein)
            let last = null;
            for (const m of remaining) {
                if (!last || (m.timestamp || 0) > (last.timestamp || 0)) {
                    last = m;
                }
            }
            console.log('[chat] delete: chat-list-update', { chatId, remainingCount: remaining.length, lastId: last?.id, lastTimestamp: last?.timestamp });

            if (last && last.timestamp) {
                chat.lastMessageId        = last.id;
                chat.lastMessageAt        = last.timestamp;
                chat.lastMessageText      = last.originalText || '';
                chat.lastMessageEncrypted = last.encrypted ? 1 : 0;
                chat.lastMessageIv        = last.iv || null;
                chat.lastMessageSender    = last.senderPhone;
                chat.lastMessageLang      = last.senderLang;
                chat.lastMessageStatus    = last.status;
            } else if (!remaining.length) {
                // Wirklich keine Message mehr -> alle Felder leeren
                chat.lastMessageId = chat.lastMessageAt = chat.lastMessageText = null;
                chat.lastMessageEncrypted = 0;
                chat.lastMessageIv = chat.lastMessageSender = chat.lastMessageLang = chat.lastMessageStatus = null;
            }
            // Wenn last existiert aber timestamp falsy: alten Wert behalten (defensive)
            this.renderChatList?.();
        }

        try {
            const res = await fetch(VCS.Chat.API_BASE + '/messages/delete', {
                method : 'POST',
                headers: VCS.chat?.authHeaders({'Content-Type': 'application/json'}),
                body   : JSON.stringify({
                    messageId: msg.id,
                    chatId   : VCS.state.chatActiveChatId,
                    mode
                })
            });
            console.log('[chat] delete sent', { msgId: msg.id, mode, status: res.status });
        } catch (e) {
            console.error('[chat] delete error:', e);
        }
    }

    _showDeleteChoiceSheet(isMine) {
        return new Promise(resolve => {
            const overlay = document.createElement('div');
            overlay.className = 'chat-delete-sheet-overlay';

            const trashIcon = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/></svg>`;
            const eyeOffIcon = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>`;
            const warnIcon = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>`;

            const everyoneOption = isMine
                ? `<button type="button" class="chat-delete-sheet__option chat-delete-sheet__option--danger" data-choice="everyone">
                       <div class="chat-delete-sheet__option-icon">${warnIcon}</div>
                       <div class="chat-delete-sheet__option-text">
                           <div class="chat-delete-sheet__option-label">${t('chatDeleteForEveryone')}</div>
                           <div class="chat-delete-sheet__option-hint">${t('chatDeleteForEveryoneHint')}</div>
                       </div>
                   </button>`
                : '';

            overlay.innerHTML = `
                <div class="chat-delete-sheet" role="dialog" aria-modal="true" aria-labelledby="chatDeleteSheetTitle">
                    <div class="chat-delete-sheet__header">
                        <div class="chat-delete-sheet__icon">${trashIcon}</div>
                        <div class="chat-delete-sheet__title" id="chatDeleteSheetTitle">${t('chatDeleteConfirm')}</div>
                        <div class="chat-delete-sheet__subtitle">${t('chatDeleteSubtitle')}</div>
                    </div>
                    <div class="chat-delete-sheet__options">
                        <button type="button" class="chat-delete-sheet__option" data-choice="me">
                            <div class="chat-delete-sheet__option-icon">${eyeOffIcon}</div>
                            <div class="chat-delete-sheet__option-text">
                                <div class="chat-delete-sheet__option-label">${t('chatDeleteForMe')}</div>
                                <div class="chat-delete-sheet__option-hint">${t('chatDeleteForMeHint')}</div>
                            </div>
                        </button>
                        ${everyoneOption}
                    </div>
                    <button type="button" class="chat-delete-sheet__cancel" data-choice="cancel">${t('chatDeleteCancel')}</button>
                </div>
            `;
            document.body.appendChild(overlay);

            const cleanup = (choice) => {
                overlay.remove();
                document.removeEventListener('keydown', onKey);
                resolve(choice === 'cancel' ? null : choice);
            };
            const onKey = (e) => { if (e.key === 'Escape') cleanup('cancel'); };
            document.addEventListener('keydown', onKey);

            overlay.addEventListener('click', (e) => {
                const btn = e.target.closest('[data-choice]');
                if (btn) {
                    cleanup(btn.dataset.choice);
                } else if (e.target === overlay) {
                    cleanup('cancel');
                }
            });
        });
    }

    onMessageReceived(msg) {
        const chatId = msg.chatId;
        if (!chatId) {
            return;
        }
        const deferredVideo = VCS.chatVideo?.isDeferredVideoMessage?.(msg);

        // Fertige Concierge-Antwort: die live gestreamte Blase mit derselben ID
        // ist nur ein Platzhalter -- raus damit, die echte Nachricht folgt.
        // Sonst griffe der Dubletten-Schutz direkt darunter und die Blase
        // bliebe im Streaming-Zustand stehen.
        const streamIdx = VCS.state.chatMessages[chatId]?.findIndex(m => m.id === msg.id && m._streaming) ?? -1;
        if (streamIdx !== -1) {
            VCS.state.chatMessages[chatId].splice(streamIdx, 1);
        }

        if (VCS.state.chatMessages[chatId] && VCS.state.chatMessages[chatId].some(m => m.id === msg.id)) {
            return;
        }

        if (msg.senderPhone === VCS.state.chatMyPhone && VCS.state.chatMessages[chatId]) {
            const tempIdx = VCS.state.chatMessages[chatId].findIndex(m => {
                if (!m._sending) {
                    return false;
                }
                if (msg.mediaFileName && m.mediaFileName) {
                    return m.mediaFileName === msg.mediaFileName;
                }
                return m.originalText === msg.originalText;
            });
            if (tempIdx !== -1) {
                VCS.state.chatMessages[chatId].splice(tempIdx, 1);
            }
        }

        if (!VCS.state.chatMessages[chatId]) {
            VCS.state.chatMessages[chatId] = [];
        }
        if (deferredVideo) {
            msg._deferredVideoPending = true;
        }
        if (msg.mediaType === 'voicephoto' && msg.transcription) {
            try {
                const parsed = JSON.parse(msg.transcription);
                if (Array.isArray(parsed)) {
                    msg._vpWords = parsed;
                } else {
                    msg._vpWords = parsed.original || [];
                    if (parsed.translated) { msg._vpWordsTranslated = parsed.translated; msg._vpTargetLang = parsed.targetLang; }
                }
            } catch (_) {}
        }
        if (msg.mediaType === 'video' && msg.transcription) {
            try {
                const parsed = JSON.parse(msg.transcription);
                msg._videoWords = parsed.original || [];
                if (parsed.translated) { msg._videoWordsTranslated = parsed.translated; msg._videoTargetLang = parsed.targetLang; }
            } catch (_) {}
        }
        if (msg.deliveredAt) msg._deliveredAt = msg.deliveredAt;
        if (msg.readAt) msg._readAt = msg.readAt;
        // Ack, der vor der Nachricht eintraf, jetzt nachziehen.
        this.applyPendingStatus(msg);
        VCS.state.chatMessages[chatId].push(msg);
        // Live empfangenen Sticker im aktiven Chat einmalig poppen lassen.
        // Nur fremde Nachrichten: eigene poppt bereits sendSticker (sonst doppelt).
        if (msg.senderPhone !== VCS.state.chatMyPhone
            && chatId === VCS.state.chatActiveChatId
            && this._stickerIdFromText(msg.originalText)) {
            this._stickerPopPending.add(msg.id);
        }
        if (msg.mediaType) {
            VCS.chatMediaGallery?.invalidateCache?.(chatId);
        }
        if (deferredVideo) {
            VCS.chatVideo?.ensureVideoProcessingPoll?.(chatId);
        }

        if (msg.encrypted && !msg._decryptedText && typeof E2E !== 'undefined') {
            VCS.e2e?.decryptMessage?.(msg).catch(e => console.error('[E2E] decrypt error:', e));
        }

        if (msg.senderPhone !== VCS.state.chatMyPhone) {
            VCS.chat?.wsSend?.({
                type     : 'message-delivered',
                messageId: msg.id,
                chatId
            });

            if (VCS.state.chatActiveChatId === chatId && !deferredVideo) {
                this.markAsRead(chatId);
            } else if (!deferredVideo) {
                VCS.state.chatUnreadCounts[chatId] = (VCS.state.chatUnreadCounts[chatId] || 0) + 1;
                this._updateChatUnreadBadge();
                // Notification verzögern wenn Übersetzung erwartet wird
                const expectsTranslation = !msg.encrypted && !msg.translatedText && msg.senderLang
                    && msg.senderLang !== VCS.state.currentLang && msg.originalText;
                if (expectsTranslation) {
                    // Pending-Notification merken, wird in onMessageTranslated ausgelöst
                    VCS.state._pendingTranslationNotify = VCS.state._pendingTranslationNotify || {};
                    VCS.state._pendingTranslationNotify[msg.id] = { chatId, msg, timeout: setTimeout(() => {
                        // Fallback: nach 5s trotzdem anzeigen falls Übersetzung ausbleibt
                        this._showMessageNotification(chatId, msg, msg.originalText);
                        delete VCS.state._pendingTranslationNotify[msg.id];
                    }, 5000) };
                } else {
                    const preview = msg.encrypted ? t('e2eNewMessage') : (msg.translatedText || msg.originalText ||
                        (msg.mediaType === 'image' ? t('chatPhotoPreview') : msg.mediaType === 'video' ? t('chatVideo') : msg.mediaType === 'music' ? t('chatMusic') : msg.mediaType === 'document' ? t('chatDocument') : t('chatVoiceMessage')));
                    this._showMessageNotification(chatId, msg, preview);
                }
            }
        }

        // Sicherstellen, dass Gruppen-Chat in der Liste existiert
        if (msg.isGroup && !VCS.state.chatChats.find(c => c.id === chatId)) {
            VCS.state.chatChats.push({ id: chatId, isGroup: true, _isGroup: true, _peerName: chatId, created_at: msg.timestamp });
            VCS.chatSetup?.loadChats?.();
        }

        const chatPreviewText = msg.encrypted
            ? '\u{1F512} ' + t('e2eNewMessage')
            : (msg.translatedText || msg.originalText || (msg.mediaType === 'image' ? '\u{1F4F7} ' + t('chatPhotoPreview') :
                msg.mediaType === 'video' ? '\u{1F3AC} ' + t('chatVideo') : msg.mediaType === 'music' ? '\u{1F3B5} ' + t('chatMusic') : msg.mediaType === 'document' ? '\u{1F4C4} ' + t('chatDocument') : t('chatVoiceMessage')));
        this.updateChatPreview(chatId, chatPreviewText, msg.timestamp, msg.senderPhone, msg.status || 'delivered');

        if (VCS.state.chatActiveChatId === chatId) {
            this.renderChatMessages();
            this.scrollToBottom();
        }

        if (msg.senderPhone && msg.senderPhone !== VCS.state.chatMyPhone) {
            const prev = VCS.state.chatActivityMap[msg.senderPhone];
            if (prev?.timeout) {
                clearTimeout(prev.timeout);
            }
            delete VCS.state.chatActivityMap[msg.senderPhone];
            VCS.chatPresence?.updateContactSubtitle?.(msg.senderPhone);
        }
        if (msg.senderPhone !== VCS.state.chatMyPhone && VCS.state.chatActiveChatId === chatId) {
            $('#chatTypingIndicator')[0]?.classList.add('hidden');
            $('#chatRecordingIndicator')[0]?.classList.add('hidden');
        }

        if (VCS.nav?.isChatListVisible()) {
            this.renderChatList();
        }
    }

    // Concierge-Antwort waechst live mit (2026-10-15). Der Server schickt
    // hoechstens alle 100 ms den bisherigen Gesamttext. Die Blase ist ein
    // Platzhalter mit _streaming; die fertige Nachricht (message-new, gleiche
    // ID) ersetzt sie in onMessageReceived.
    onBotDelta(msg) {
        const chatId = msg.chatId;
        if (!chatId || !msg.id || typeof msg.text !== 'string') return;
        const isActive = VCS.state.chatActiveChatId === chatId;
        // Chat nie geladen und nicht offen: nichts zu zeigen. Die fertige
        // Nachricht kommt ohnehin.
        if (!VCS.state.chatMessages[chatId]) {
            if (!isActive) return;
            VCS.state.chatMessages[chatId] = [];
        }
        const msgs = VCS.state.chatMessages[chatId];
        let m = msgs.find(x => x.id === msg.id);
        // Die fertige Nachricht war schneller -- nichts mehr anfassen.
        if (m && !m._streaming) return;

        const isNew = !m;
        if (isNew) {
            m = {
                id           : msg.id,
                chatId,
                senderPhone  : msg.senderPhone || VCS.Chat.BOT_PHONE,
                senderLang   : msg.senderLang || null,
                originalText : msg.text,
                timestamp    : Date.now(),
                status       : 'read',
                _streaming   : true,
                _translationDone: true
            };
            msgs.push(m);
        } else {
            m.originalText = msg.text;
        }
        if (!isActive) return;

        // Ab dem ersten Wort steht die Blase -- "schreibt..." waere doppelt.
        $('#chatTypingIndicator')[0]?.classList.add('hidden');

        // Nur mitscrollen, wenn man schon unten war. Wer hochgescrollt hat,
        // um etwas nachzulesen, wird nicht weggerissen.
        const container = $('#chatMessages')[0];
        const wasAtBottom = !container
            || (container.scrollHeight - container.scrollTop - container.clientHeight) < 80;

        const textEl = isNew ? null
            : container?.querySelector(`.chat-bubble[data-msg-id="${CSS.escape(msg.id)}"] .chat-bubble-text`);
        if (textEl) {
            // Nur den Text tauschen statt die ganze Liste neu zu bauen.
            textEl.innerHTML = parseMarkdown(msg.text);
        } else {
            this.renderChatMessages();
        }
        if (wasAtBottom && container) {
            container.scrollTop = container.scrollHeight;
        }
    }

    // Stream abgebrochen (n8n-Fehler, leere Antwort). Ohne ID: alle laufenden
    // Bot-Blasen dieses Chats entfernen -- der Server kennt die ID dann nicht.
    onBotStreamAbort(msg) {
        const msgs = VCS.state.chatMessages[msg.chatId];
        if (!msgs) return;
        const before = msgs.length;
        VCS.state.chatMessages[msg.chatId] = msgs.filter(m =>
            !(m._streaming && (!msg.id || m.id === msg.id)));
        if (VCS.state.chatMessages[msg.chatId].length !== before
            && VCS.state.chatActiveChatId === msg.chatId) {
            this.renderChatMessages();
        }
    }

    onMessageTranslated(msg) {
        const msgs = VCS.state.chatMessages[msg.chatId];
        const m = msgs?.find(x => x.id === msg.messageId);
        if (m) {
            m.translatedText = msg.translatedText;
            m._translationDone = true;
            if (m._translationPendingTimer) {
                clearTimeout(m._translationPendingTimer);
                m._translationPendingTimer = null;
            }
            if (VCS.state.chatActiveChatId === msg.chatId) {
                this.renderChatMessages();
                this.scrollToBottom();
            }
        }
        // Chat-Liste Preview mit Uebersetzung aktualisieren (auch wenn Messages nicht geladen)
        if (msg.translatedText) {
            this.updateChatPreview(msg.chatId, msg.translatedText, m?.timestamp || Date.now(), m?.senderPhone, m?.status || 'delivered');
        }
        // Wartende Notification mit Uebersetzung anzeigen
        const pending = VCS.state._pendingTranslationNotify?.[msg.messageId];
        if (pending && msg.translatedText) {
            clearTimeout(pending.timeout);
            this._showMessageNotification(pending.chatId, pending.msg, msg.translatedText);
            delete VCS.state._pendingTranslationNotify[msg.messageId];
        }
    }

    // Server meldet: Uebersetzung ist endgueltig nicht moeglich (Skip oder Fehler
    // nach Retry). Platzhalter "Uebersetzung laeuft..." in der Bubble ausblenden.
    onMessageTranslationFailed(msg) {
        const msgs = VCS.state.chatMessages[msg.chatId];
        const m = msgs?.find(x => x.id === msg.messageId);
        if (!m) {
            console.log('[chat-translate] failed fuer unbekannte msg', msg.messageId, msg.reason || '');
            return;
        }
        m._translationDone = true;
        // Gleiche Sprache: bewusst nicht uebersetzt, kein Fehler.
        m._translationFailed = msg.reason !== 'skipped-same-lang';
        if (m._translationPendingTimer) {
            clearTimeout(m._translationPendingTimer);
            m._translationPendingTimer = null;
        }
        console.log('[chat-translate] failed msg=' + msg.messageId + ' reason=' + (msg.reason || 'unknown'));
        if (VCS.state.chatActiveChatId === msg.chatId) {
            this.renderChatMessages();
        }
        // Wartende Notification aufloesen (Fallback: Original-Text verwenden)
        const pending = VCS.state._pendingTranslationNotify?.[msg.messageId];
        if (pending) {
            clearTimeout(pending.timeout);
            const preview = pending.msg.originalText || '';
            if (preview) this._showMessageNotification(pending.chatId, pending.msg, preview);
            delete VCS.state._pendingTranslationNotify[msg.messageId];
        }
    }

    // Hilfs-Check: soll der "Uebersetzung laeuft..."-Platzhalter gezeigt werden?
    // Wird in den drei Bubble-Renderpfaden (Text/Voice/Media) benutzt.
    _shouldShowTranslatingPlaceholder(msg) {
        if (!msg) return false;
        if (msg.encrypted) return false;
        if (msg.translatedText) return false;
        if (msg._translationDone) return false;
        if (msg._sending) return false;
        // Bot-Chat: keine Uebersetzung
        if (VCS.Chat.isBotChat(msg.chatId)) return false;
        // Empfangene Nachricht in eigener Sprache: keine Uebersetzung noetig
        if (msg.senderPhone !== VCS.state.chatMyPhone && msg.senderLang && msg.senderLang === (VCS.state.currentLang || 'de')) return false;
        // Gruppen-Chats: kein Pending-Platzhalter (nur fuer 1:1)
        if (msg.isGroup) return false;
        // Alte Nachrichten aus History: kein Server-Event mehr zu erwarten.
        // Nur bei frischen Nachrichten (< 2 Min alt) Platzhalter zeigen.
        const age = Date.now() - (Number(msg.timestamp) || 0);
        if (age > 120000) return false;
        const text = msg._decryptedText || msg.originalText || msg.transcription || '';
        if (!text.trim()) return false;
        // Emoji/Zahlen-only: Server uebersetzt nicht -> kein Platzhalter
        const withoutEmojis = text.replace(/[\p{Emoji_Presentation}\p{Extended_Pictographic}\uFE0F\u200D]/gu, '').trim();
        if (!withoutEmojis || /^[\d\s\p{P}\p{S}]*$/u.test(withoutEmojis)) return false;
        // Lokaler Fallback-Timeout: falls WS-Event verloren geht (Reconnect etc.)
        // entfernt der Timer den Platzhalter nach 90 Sekunden.
        if (!msg._translationPendingTimer) {
            const msgRef = msg;
            const chatId = msg.chatId;
            msgRef._translationPendingTimer = setTimeout(() => {
                msgRef._translationDone = true;
                msgRef._translationFailed = true;
                msgRef._translationPendingTimer = null;
                console.log('[chat-translate] local-timeout fuer msg=' + msgRef.id);
                if (VCS.state.chatActiveChatId === chatId) {
                    this.renderChatMessages();
                }
            }, 90000);
        }
        return true;
    }

    onVoiceTranslationReady(msg) {
        const msgs = VCS.state.chatMessages[msg.chatId];
        const m = msgs?.find(x => x.id === msg.messageId);
        if (m) {
            if (msg.transcription) { m.transcription = msg.transcription; }
            if (msg.translatedText) { m.translatedText = msg.translatedText; }
            if (msg.translatedAudioUrl) { m.translatedAudioUrl = msg.translatedAudioUrl; }
            if (msg.translatedAudioDurationMs) { m.translatedAudioDurationMs = msg.translatedAudioDurationMs; }
            if (msg.translatedText) {
                m._translationDone = true;
                if (m._translationPendingTimer) { clearTimeout(m._translationPendingTimer); m._translationPendingTimer = null; }
            }
            if (VCS.state.chatActiveChatId === msg.chatId) {
                this.renderChatMessages();
                this.scrollToBottom();
            }
        }
        // Chat-Liste Preview mit Uebersetzung aktualisieren (auch wenn Messages nicht geladen)
        if (msg.translatedText) {
            this.updateChatPreview(msg.chatId, msg.translatedText, m?.timestamp || Date.now(), m?.senderPhone, m?.status || 'delivered');
        }
    }

    onVideoTranslationReady(msg) {
        const msgs = VCS.state.chatMessages[msg.chatId];
        if (!msgs) {
            return;
        }
        const m = msgs.find(x => x.id === msg.messageId);
        if (!m) {
            return;
        }

        if (msg.translatedVideoUrl) { m.translatedVideoUrl = msg.translatedVideoUrl; }
        if (msg.translatedVideoLang) { m.translatedVideoLang = msg.translatedVideoLang; }
        m.translatedVideoStatus = msg.translatedVideoStatus || 'ready';
        const becameVisibleNow = !!m._deferredVideoPending && m.translatedVideoStatus !== 'processing';
        if (becameVisibleNow) {
            m._deferredVideoPending = false;
            if (m.senderPhone !== VCS.state.chatMyPhone) {
                if (VCS.state.chatActiveChatId === msg.chatId) {
                    this.markAsRead(msg.chatId);
                } else {
                    VCS.state.chatUnreadCounts[msg.chatId] = (VCS.state.chatUnreadCounts[msg.chatId] || 0) + 1;
                    this._updateChatUnreadBadge();
                    const chat = VCS.state.chatChats.find(c => c.id === msg.chatId);
                    const senderName = chat?._peerName || m.senderPhone;
                    const preview = m.translatedText || m.originalText || t('chatVideo');
                    VCS.ui?.showToast?.(senderName + ': ' + preview, 'info');
                }
            }
        }
        if (m.senderPhone !== VCS.state.chatMyPhone) {
            m._preferTranslatedVideo = true;
        }

        if (VCS.state.chatActiveChatId === msg.chatId) {
            this.renderChatMessages();
            this.scrollToBottom();
        }
        if (m.translatedVideoStatus === 'processing') {
            VCS.chatVideo?.ensureVideoProcessingPoll?.(msg.chatId);
        }
    }

    // Rangfolge der Ampel: sent < delivered < read. Ein Status darf nur nach oben
    // wandern — Zustell- und Lese-Ack kommen nicht garantiert in dieser Reihenfolge
    // an, ein spaetes 'delivered' darf ein bereits gesetztes 'read' nicht kippen.
    _statusRank(status) {
        const ranks = { sent: 0, delivered: 1, read: 2 };
        return ranks[status] ?? -1;
    }

    // Nachricht fuer einen Ack suchen: erst im gemeldeten Chat, sonst in allen
    // geladenen. Der zweite Weg faengt Acks ab, deren chatId nicht zu der passt,
    // unter der der Client die Nachricht fuehrt.
    _findMessageForStatus(messageId, chatId) {
        const direct = VCS.state.chatMessages[chatId];
        if (Array.isArray(direct)) {
            const m = direct.find(x => x.id === messageId);
            if (m) return { msg: m, chatId };
        }
        for (const cid of Object.keys(VCS.state.chatMessages || {})) {
            if (cid === chatId || !Array.isArray(VCS.state.chatMessages[cid])) continue;
            const m = VCS.state.chatMessages[cid].find(x => x.id === messageId);
            if (m) {
                VCS.logRemote?.('warn', '[msg-status] Ack mit fremder chatId:', { messageId, gemeldet: chatId, gefunden: cid });
                return { msg: m, chatId: cid };
            }
        }
        return null;
    }

    // Ack ohne Ziel merken (hoechster Rang gewinnt), damit er beim Einfuegen
    // der Nachricht nachgezogen werden kann.
    _rememberPendingStatus(messageId, status, readAt, deliveredAt) {
        if (!messageId) return;
        if (!this._pendingStatus) this._pendingStatus = {};
        const entry = this._pendingStatus[messageId] || {};
        if (this._statusRank(status) > this._statusRank(entry.status)) entry.status = status;
        if (readAt) entry.readAt = readAt;
        if (deliveredAt) entry.deliveredAt = deliveredAt;
        this._pendingStatus[messageId] = entry;
        // Nie eingeloeste Eintraege nicht unbegrenzt sammeln.
        const keys = Object.keys(this._pendingStatus);
        if (keys.length > 200) delete this._pendingStatus[keys[0]];
    }

    // Fortschritts-Bubble waehrend des Uploads.
    renderUploadProgress(msg) {
        const pct = Math.max(0, Math.min(100, Math.round(msg._uploadProgress || 0)));
        return `<div class="chat-media-upload" data-upload-progress="${escapeHtml(msg.id)}">
            <div class="chat-media-upload-head">
                <span class="chat-media-upload-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M12 19V5"></path><path d="m5 12 7-7 7 7"></path>
                    </svg>
                </span>
                <span class="chat-media-upload-text">${escapeHtml(t('chatVideoUploading'))}</span>
                <span class="chat-media-upload-pct">${pct}&thinsp;%</span>
            </div>
            <div class="chat-media-upload-bar"><div class="chat-media-upload-bar-fill" style="width:${pct}%"></div></div>
        </div>`;
    }

    // Balken direkt am DOM nachziehen - ein voller Re-Render bei jedem
    // Progress-Event waere zu teuer. Bei 100 % einmal neu rendern, dann
    // uebernimmt die Verarbeitungs-Bubble.
    updateUploadProgress(messageId, pct) {
        if (pct >= 100) {
            this.renderChatMessages();
            return;
        }
        const el = document.querySelector(`[data-upload-progress="${messageId}"]`);
        if (!el) return;
        const fill = el.querySelector('.chat-media-upload-bar-fill');
        const label = el.querySelector('.chat-media-upload-pct');
        if (fill) fill.style.width = pct + '%';
        if (label) label.innerHTML = pct + '&thinsp;%';
    }

    // Gepufferten Ack auf eine gerade eingefuegte Nachricht anwenden.
    applyPendingStatus(msg) {
        const p = msg && this._pendingStatus?.[msg.id];
        if (!p) return false;
        delete this._pendingStatus[msg.id];
        if (p.readAt) msg._readAt = p.readAt;
        if (p.deliveredAt) msg._deliveredAt = p.deliveredAt;
        if (this._statusRank(p.status) > this._statusRank(msg.status)) {
            msg.status = p.status;
        }
        VCS.logRemote?.('info', '[msg-status] nachgezogen:', { messageId: msg.id, status: msg.status });
        return true;
    }

    // Setzt den Zustell-Status und gibt die chatId zurueck, unter der die
    // Nachricht tatsaechlich liegt (die Chat-Liste braucht dieselbe).
    updateMessageStatus(messageId, chatId, status, readAt, deliveredAt) {
        const found = this._findMessageForStatus(messageId, chatId);
        if (!found) {
            this._rememberPendingStatus(messageId, status, readAt, deliveredAt);
            VCS.logRemote?.('warn', '[msg-status] kein Ziel, gepuffert:', { messageId, chatId, status });
            return null;
        }
        const msg = found.msg;
        if (readAt) msg._readAt = readAt;
        if (deliveredAt) msg._deliveredAt = deliveredAt;
        if (this._statusRank(status) > this._statusRank(msg.status)) {
            msg.status = status;
        }
        if (VCS.state.chatActiveChatId === found.chatId) {
            this.renderChatMessages();
        }
        return found.chatId;
    }

    updateChatListStatus(chatId, status) {
        const chat = VCS.state.chatChats.find(c => c.id === chatId);
        if (chat && chat.lastMessageSender === VCS.state.chatMyPhone) {
            if (this._statusRank(status) <= this._statusRank(chat.lastMessageStatus)) {
                return;
            }
            chat.lastMessageStatus = status;
            this.renderChatList();
        }
    }

    // ── Gruppen Read-Tracking ────────────────────────────────

    trackGroupReader(chatId, messageIds, readerPhone) {
        if (!VCS.state._groupReaders) VCS.state._groupReaders = {};
        for (const msgId of messageIds) {
            if (!VCS.state._groupReaders[msgId]) VCS.state._groupReaders[msgId] = new Set();
            VCS.state._groupReaders[msgId].add(readerPhone);
        }
        if (VCS.state.chatActiveChatId === chatId) {
            this.renderChatMessages();
        }
    }

    ensureMinStatus(messageId, chatId, minStatus) {
        const found = this._findMessageForStatus(messageId, chatId);
        if (!found) {
            this._rememberPendingStatus(messageId, minStatus);
            return;
        }
        if (this._statusRank(minStatus) > this._statusRank(found.msg.status)) {
            found.msg.status = minStatus;
            if (VCS.state.chatActiveChatId === found.chatId) {
                this.renderChatMessages();
            }
        }
    }

    async showReadersPopup(messageId, anchorEl) {
        try {
            const res = await fetch(VCS.Chat.API_BASE + '/messages/' + encodeURIComponent(messageId) + '/readers', {
                headers: VCS.chat?.authHeaders()
            });
            if (!res.ok) return;
            const { readers } = await res.json();
            const t = (k) => VCS.i18n?.t(k) || k;

            // Bestehendes Popup entfernen
            document.querySelector('.read-receipts-popup')?.remove();

            const popup = document.createElement('div');
            popup.className = 'read-receipts-popup';

            let html = `<div class="read-receipts-header">${t('readBy')}</div>`;
            if (readers.length === 0) {
                html += `<div class="read-receipts-empty">${t('readByNobody')}</div>`;
            } else {
                for (const r of readers) {
                    const name = r.displayName || r.phone;
                    const time = VCS.chat?.formatTime?.(r.readAt) || '';
                    html += `<div class="read-receipts-item">
                        <span class="read-receipts-name">${escapeHtml(name)}</span>
                        <span class="read-receipts-time">${time}</span>
                    </div>`;
                }
            }
            popup.innerHTML = html;
            document.body.appendChild(popup);

            // Position relativ zum Anker
            const rect = anchorEl.getBoundingClientRect();
            popup.style.bottom = (window.innerHeight - rect.top + 4) + 'px';
            popup.style.right = (window.innerWidth - rect.right) + 'px';

            // Schließen bei Klick außerhalb
            const close = (e) => {
                if (!popup.contains(e.target)) {
                    popup.remove();
                    document.removeEventListener('pointerdown', close);
                }
            };
            setTimeout(() => document.addEventListener('pointerdown', close), 10);
        } catch (e) {
            console.error('[chat] showReadersPopup error:', e);
        }
    }

    updateChatPreview(chatId, text, timestamp, senderPhone, status) {
        const chat = VCS.state.chatChats.find(c => c.id === chatId);
        if (chat) {
            chat.lastMessageText = text;
            chat.lastMessageAt = timestamp;
            if (senderPhone) chat.lastMessageSender = senderPhone;
            if (status) chat.lastMessageStatus = status;
            this.renderChatList();
        }
    }

    _showMessageNotification(chatId, msg, preview) {
        // Ruhemodus: Nachricht steht still in der Liste, kein Toast, keine Meldung
        if (VCS.notify?.isQuiet?.()) {
            return;
        }
        const chat = VCS.state.chatChats.find(c => c.id === chatId);
        const isGroupMsg = msg.isGroup || chat?._isGroup || chat?.isGroup;
        let senderName;
        if (isGroupMsg) {
            const personName = msg.senderName || this._getGroupSenderName(msg.senderPhone);
            senderName = (chat?._peerName || '?') + ' — ' + personName;
        } else {
            senderName = chat?._peerName || msg.senderPhone;
        }
        VCS.ui?.showToast?.(senderName + ': ' + preview, 'info');
        if (document.hidden && VCS.state.notifyEnabled && 'Notification' in window && Notification.permission === 'granted') {
            try {
                new Notification(senderName, {
                    body: preview,
                    icon: '/webcfg/web-app-manifest-192x192.png',
                    tag : 'chat-' + chatId
                });
            } catch (_) {}
        }
    }

    renderReactionBadges(msg) {
        if (!msg.reactions || Object.keys(msg.reactions).length === 0) {
            return '';
        }
        let html = '<div class="chat-reactions">';
        for (const [emoji, phones] of Object.entries(msg.reactions)) {
            const isMine = phones.includes(VCS.state.chatMyPhone);
            const cls = isMine ? 'chat-reaction-badge chat-reaction-badge--mine' : 'chat-reaction-badge';
            const count = phones.length > 1 ? `<span class="reaction-count">${phones.length}</span>` : '';
            // Sticker-Reaktion (Token "sticker:NN") als kleines Bild, sonst Emoji-Text.
            const sid = this._stickerIdFromText(emoji);
            let inner = emoji;
            if (sid) {
                const surl = this._stickerUrl(sid);
                inner = surl ? `<img class="chat-reaction-sticker" src="${escapeHtml(surl)}" alt="Sticker">` : escapeHtml(emoji);
            }
            html += `<span class="${cls}" data-emoji="${escapeHtml(emoji)}" data-msg-id="${msg.id}">${inner}${count}</span>`;
        }
        html += '</div>';
        return html;
    }

    // Klick auf den Emoji-Button. Desktop (Telegram-Stil): rechte Seitenleiste
    // togglen. Mobil: Leiste unten ein-/ausblenden.
    toggleEmojiPicker() {
        if (this._isDesktopEmoji()) {
            this.toggleEmojiSidebar();
            return;
        }
        const wrap = $('#emojiPickerWrap')[0];
        if (!wrap) {
            return;
        }
        if (wrap.classList.contains('hidden')) {
            this._showEmojiPopup(wrap);
        } else {
            this.closeEmojiPicker();
        }
    }

    _isDesktopEmoji() {
        return window.matchMedia('(min-width: 993px)').matches;
    }

    // Oeffnet das schwebende Popup (#emojiPickerWrap). Mobil zusaetzlich die
    // Tastatur-Logik (iOS Fake-Cursor, Android inputmode=none). Desktop nur
    // Positionierung knapp ueber der Eingabezeile (Hover-Vorschau).
    _showEmojiPopup(wrap) {
        // Falls der Picker gerade der Tastatur weicht: abbrechen und Reste entfernen.
        this._cancelEmojiKeyboardClose();
        wrap.classList.remove('emoji-picker-wrap--closing');
        wrap.style.maxHeight = '';
        const inputEl = $('#chatTextInput')[0];
        if (inputEl) {
            this._emojiCursorPos = inputEl.selectionStart ?? inputEl.value.length;
            const isIOS = this._isIOS();
            const isMobile = isIOS || /Android/i.test(navigator.userAgent);
            if (isIOS) {
                // iOS-Safari ignoriert inputmode=none beim Fokus und holt die Tastatur
                // zurueck -> hier nur blurren. Nativer Cursor ist ohne Fokus nicht
                // moeglich, daher zeichnen wir einen Fake-Cursor.
                inputEl.blur();
                this._showFakeCaret();
            } else if (isMobile) {
                // Android: Tastatur unterdruecken, aber Fokus + blinkenden Cursor behalten.
                // inputmode=none haelt die Soft-Tastatur unten, das Feld bleibt fokussiert.
                inputEl.inputMode = 'none';
                const pos = this._emojiCursorPos;
                // Guard verhindert, dass der focus-Listener den Picker sofort schliesst.
                this._emojiKeepFocus = true;
                inputEl.blur();
                inputEl.focus();
                inputEl.setSelectionRange(pos, pos);
            }
        }
        // Picker bei jedem Oeffnen neu erstellen fuer aktuelle Favorites.
        wrap.innerHTML = '';
        wrap.appendChild(this._buildEmojiPicker());
        // Desktop: Popup schwebt knapp ueber der Eingabezeile (bottom dynamisch
        // an deren Hoehe). Mobil bleibt die CSS-Leiste unten -> bottom leeren.
        if (this._isDesktopEmoji()) {
            const bar = $('.chat-input-bar')[0];
            wrap.style.bottom = bar ? (bar.offsetHeight + 8) + 'px' : '';
        } else {
            wrap.style.bottom = '';
            // Mobil ersetzt der Picker die Tastatur -> untere Navigation ausblenden.
            document.body.classList.add('emoji-panel-open');
        }
        wrap.classList.remove('hidden');
    }

    // Hover ueber den Emoji-Button (Desktop): Popup zeigen, sofern die
    // Seitenleiste nicht ohnehin offen ist.
    _openEmojiHoverPopup() {
        if (this._emojiSidebarOpen) {
            return;
        }
        const wrap = $('#emojiPickerWrap')[0];
        if (!wrap || !wrap.classList.contains('hidden')) {
            return;
        }
        this._showEmojiPopup(wrap);
    }

    // Schliesst das Hover-Popup verzoegert, damit der Wechsel Button->Popup
    // (kleine Luecke) das Popup nicht sofort verschwinden laesst.
    _scheduleEmojiHoverClose() {
        clearTimeout(this._emojiHoverTimer);
        this._emojiHoverTimer = setTimeout(() => {
            this.closeEmojiPicker();
        }, 200);
    }

    // Rechte Emoji-Seitenleiste (Desktop, Telegram-Stil).
    toggleEmojiSidebar() {
        if (this._emojiSidebarOpen) {
            this.closeEmojiSidebar();
        } else {
            this.openEmojiSidebar();
        }
    }

    openEmojiSidebar() {
        const sidebar = $('#emojiSidebar')[0];
        if (!sidebar) {
            return;
        }
        // Nicht beides gleichzeitig: schwebendes Hover-Popup schliessen.
        clearTimeout(this._emojiHoverTimer);
        this.closeEmojiPicker();
        const inputEl = $('#chatTextInput')[0];
        if (inputEl) {
            this._emojiCursorPos = inputEl.selectionStart ?? inputEl.value.length;
        }
        sidebar.innerHTML = '';
        sidebar.appendChild(this._buildEmojiPicker());
        sidebar.classList.remove('hidden');
        this._emojiSidebarOpen = true;
        $('#chatEmojiBtn')[0]?.classList.add('chat-emoji-btn--active');
    }

    closeEmojiSidebar() {
        const sidebar = $('#emojiSidebar')[0];
        if (sidebar) {
            sidebar.classList.add('hidden');
            sidebar.innerHTML = '';
        }
        this._emojiSidebarOpen = false;
        $('#chatEmojiBtn')[0]?.classList.remove('chat-emoji-btn--active');
    }

    // Baut ein vollstaendig konfiguriertes <emoji-picker> (Locale, i18n, Theme).
    // onPick(emoji) wird bei Auswahl aufgerufen. Gemeinsame Factory fuer das
    // Chat-Input und das Media-Preview-Caption-Feld.
    // ── Sticker-Effekt (Telegram-Stil Partikel-Burst) ────────────────
    // Beim ersten Erscheinen eines Stickers laeuft die CSS-Animation
    // "chat-sticker-pop". Ein einziger delegierter animationstart-Listener
    // faengt deren Start ab und sprueht kurz Emoji-Partikel um den Sticker.
    // Vorteile: kein Trigger nach jedem Render noetig, funktioniert
    // automatisch fuer jeden neu eingefuegten Sticker (animationstart
    // bubbelt), und bei prefers-reduced-motion laeuft die Animation gar
    // nicht -> dann auch kein Partikel-Effekt.
    _ensureStickerFxListener() {
        if (this._stickerFxBound) {
            return;
        }
        this._stickerFxBound = true;
        document.addEventListener('animationstart', (e) => {
            if (e.animationName !== 'chat-sticker-pop') {
                return;
            }
            const img = e.target;
            if (img && img.classList && img.classList.contains('chat-sticker')) {
                this._burstStickerFx(img);
            }
        });
    }

    // Waehlt den Partikel-Emoji-Satz passend zum Sticker-Motiv (per Dateiname
    // im src). Ohne Treffer ein neutraler Funkel-Default, der zu allem passt.
    _stickerFxEmojis(img) {
        const src = (img.getAttribute('src') || '').toLowerCase();
        if (/(herz|kuss|umarm|kuschel|liebe|prost)/.test(src)) {
            return ['❤️', '💕', '💖', '💗'];
        }
        if (/(lach|jubel|begeistert|zufrieden|peace|urlaub|daumen)/.test(src)) {
            return ['🎉', '✨', '⭐', '🎊'];
        }
        if (/(traurig|weinen|muede)/.test(src)) {
            return ['💧', '✨', '💫'];
        }
        if (/wuetend/.test(src)) {
            return ['💢', '🔥', '✨'];
        }
        return ['✨', '⭐', '💫', '🌟'];
    }

    // Sprueht kurz Emoji-Partikel radial vom Sticker weg. Die Partikel haengen
    // am .chat-sticker-wrap und raeumen sich nach ihrer Animation selbst ab.
    _burstStickerFx(img) {
        const wrap = img.parentElement;
        if (!wrap || wrap.querySelector('.sticker-fx-particle')) {
            return; // schon aktiv -> nicht doppelt spawnen
        }
        const emojis = this._stickerFxEmojis(img);
        const count = 18;
        for (let i = 0; i < count; i++) {
            // Radiale Streuung mit leichtem Aufwaerts-Bias (Partikel fliegen
            // bevorzugt nach oben-aussen, wie in Telegram).
            const base = (i / count) * Math.PI * 2;
            const angle = base + (Math.random() - 0.5) * 0.6 - Math.PI / 2;
            const dist = 85 + Math.random() * 75;
            const tx = Math.cos(angle) * dist;
            const ty = Math.sin(angle) * dist - 16;
            const rot = (Math.random() - 0.5) * 180;

            const p = document.createElement('span');
            p.className = 'sticker-fx-particle';
            p.textContent = emojis[i % emojis.length];
            p.style.setProperty('--tx', tx.toFixed(1) + 'px');
            p.style.setProperty('--ty', ty.toFixed(1) + 'px');
            p.style.setProperty('--rot', rot.toFixed(1) + 'deg');
            // Enge Staffelung -> Partikel starten fast gleichzeitig (Knall-Effekt).
            p.style.animationDelay = (i * 12) + 'ms';
            p.addEventListener('animationend', () => p.remove());
            wrap.appendChild(p);
        }
    }

    // ── Sticker-Support ──────────────────────────────────────────────
    // Eigene Sticker (PNG) erscheinen als Kategorie im Emoji-Picker und werden
    // als Token "sticker:NN" (NN = 2-stellige ID) verschickt bzw. als Reaktion
    // gespeichert. Das Rendering loest die ID ueber das Manifest zur Bild-URL auf.
    _stickerBase() {
        return (this._stickerManifest && this._stickerManifest.base) || 'emoji/stickers/';
    }

    // Laedt das Sticker-Manifest einmalig (Cache). Liefert ein Promise auf die Daten.
    _loadStickerManifest() {
        if (this._stickerManifest) {
            return Promise.resolve(this._stickerManifest);
        }
        if (!this._stickerManifestPromise) {
            // Cache-Buster per Timestamp: erzwingt bei jedem Laden die aktuelle
            // stickers.json, auch wenn ein alter Service Worker sie Cache-First
            // ausliefern wuerde. Neue Sticker greifen so ohne Versions-Bump.
            this._stickerManifestPromise = fetch('emoji/stickers.json?v=' + Date.now())
                .then(r => r.json())
                .then(m => { this._stickerManifest = m; this._buildStickerIndex(m); return m; })
                .catch(e => { console.warn('[chat] Sticker-Manifest laden fehlgeschlagen:', e); return null; });
        }
        return this._stickerManifestPromise;
    }

    // Baut einen flachen Index id -> {file, name, packId} aus der Pack-Struktur,
    // damit das Rendering eine Sticker-ID ohne Pack-Suche aufloesen kann.
    _buildStickerIndex(m) {
        this._stickerIndex = {};
        const packs = (m && Array.isArray(m.packs)) ? m.packs : [];
        for (const p of packs) {
            for (const s of (p.stickers || [])) {
                this._stickerIndex[s.id] = { file: s.file, name: s.name || s.id, packId: p.id };
            }
        }
    }

    // Prueft, ob ein Text genau ein Sticker-Token ist -> liefert die ID (z.B. '04') oder null.
    _stickerIdFromText(text) {
        if (typeof text !== 'string') {
            return null;
        }
        const m = text.trim().match(/^sticker:(\d{2})$/);
        return m ? m[1] : null;
    }

    // Baut die Bild-URL fuer eine Sticker-ID aus dem Index (null, wenn unbekannt).
    _stickerUrl(id) {
        const entry = this._stickerIndex && this._stickerIndex[id];
        return entry ? (this._stickerBase() + entry.file) : null;
    }

    // ── Zuletzt benutzte Sticker (localStorage) ──────────────────────
    _getRecentStickerIds() {
        try {
            const raw = localStorage.getItem('vcs_recent_stickers');
            const arr = raw ? JSON.parse(raw) : [];
            // Nur noch existierende IDs (Manifest koennte sich geaendert haben).
            return Array.isArray(arr) ? arr.filter(id => this._stickerIndex && this._stickerIndex[id]) : [];
        } catch (_) {
            return [];
        }
    }

    _recordRecentSticker(id) {
        try {
            let arr = this._getRecentStickerIds().filter(x => x !== id);
            arr.unshift(id);
            arr = arr.slice(0, 16);
            localStorage.setItem('vcs_recent_stickers', JSON.stringify(arr));
        } catch (_) {
            // localStorage nicht verfuegbar -> Recent still ignorieren.
        }
    }

    // ── GIF-Support ──────────────────────────────────────────────────
    // GIFs sind stumme MP4-Schleifen aus emoji/gifs/. Das Backend
    // (signaling/modules/gif-scanner.js) pflegt das Manifest emoji/gifs.json
    // selbst; verschickt wird der Token "gif:N". Abspielen ohne Steuerung,
    // stumm, in Schleife, inline -- so spielt iOS Safari ohne Nutzer-Geste ab.
    _gifBase() {
        return (this._gifManifest && this._gifManifest.base) || 'emoji/gifs/';
    }

    // Laedt das GIF-Manifest einmal pro Sitzung -- und erneut, wenn der Server
    // per 'chat-authed' einen anderen Stempel meldet (onServerVersions).
    // Schlaegt das Laden fehl, bleibt der alte Stand und der naechste Aufruf
    // versucht es erneut.
    _loadGifManifest() {
        if (this._gifManifest && !this._gifManifestStale) {
            return Promise.resolve(this._gifManifest);
        }
        if (!this._gifManifestPromise) {
            // Stempel, den der Server zuletzt gemeldet hat -- gehoert zu dem,
            // was wir gleich laden.
            const version = this._gifServerVersion || '';
            this._gifManifestPromise = fetch('emoji/gifs.json?v=' + Date.now())
                .then(r => (r.ok ? r.json() : null))
                .then(m => {
                    if (m) {
                        this._gifManifest = m;
                        this._gifVersion = version;
                        this._gifManifestStale = false;
                        this._buildGifIndex(m);
                    }
                    return this._gifManifest || null;
                })
                .catch(e => {
                    console.warn('[chat] GIF-Manifest laden fehlgeschlagen:', e);
                    return this._gifManifest || null;
                })
                .finally(() => { this._gifManifestPromise = null; });
        }
        return this._gifManifestPromise;
    }

    // Aus signaling.js bei jedem 'chat-authed' (auch nach Rueckkehr aus dem
    // Hintergrund). Nur bei abweichendem Stempel als veraltet markieren; neu
    // geladen wird erst, wenn der Picker das naechste Mal aufgeht.
    onServerVersions(versions) {
        const gifs = versions && versions.gifs;
        if (!gifs) {
            return;
        }
        this._gifServerVersion = gifs;
        if (!this._gifManifest) {
            return;
        }
        if (!this._gifVersion) {
            // Manifest kam vor dem ersten 'chat-authed' -- frisch geladen,
            // also gilt der aktuelle Stempel.
            this._gifVersion = gifs;
            return;
        }
        if (this._gifVersion !== gifs) {
            console.log('[chat] GIF-Manifest veraltet (' + this._gifVersion + ' -> ' + gifs + '), lade beim naechsten Oeffnen neu');
            this._gifManifestStale = true;
        }
    }

    _buildGifIndex(m) {
        this._gifIndex = {};
        const packs = (m && Array.isArray(m.packs)) ? m.packs : [];
        for (const p of packs) {
            for (const g of (p.gifs || [])) {
                this._gifIndex[String(g.id)] = g;
            }
        }
    }

    // Prueft, ob ein Text genau ein GIF-Token ist -> liefert die ID (z.B. '12') oder null.
    _gifIdFromText(text) {
        if (typeof text !== 'string') {
            return null;
        }
        const m = text.trim().match(/^gif:(\d{1,6})$/);
        return m ? m[1] : null;
    }

    _gifEntry(id) {
        return (this._gifIndex && this._gifIndex[String(id)]) || null;
    }

    // Vorschau-Text fuer Chat-Liste/Antwort: "🎞️ GIF: <Name>" (Name aus dem
    // Title-Tag), ohne bekannten Namen nur "🎞️ GIF". Wie das Push-Label.
    _gifPreviewLabel(id) {
        const gif = this._gifEntry(id);
        const label = '\u{1F39E}\u{FE0F} ' + t('chatGif');
        return (gif && gif.name) ? label + ': ' + gif.name : label;
    }

    // Video-Tag fuer ein GIF. muted + playsinline sind fuer iOS Pflicht (sonst
    // kein Abspielen ohne Geste bzw. Vollbild). Kein autoplay-Attribut: das
    // Starten uebernimmt der IntersectionObserver, damit nur Sichtbares laeuft.
    // inBubble: echtes Seitenverhaeltnis; im Picker quadratisch beschnitten.
    _gifVideoHtml(gif, inBubble) {
        const base = this._gifBase();
        const ratio = (inBubble && gif.w && gif.h) ? ` style="aspect-ratio:${Number(gif.w)} / ${Number(gif.h)}"` : '';
        return '<video class="chat-gif-video" src="' + escapeHtml(base + gif.file) + '"' +
            (gif.poster ? ' poster="' + escapeHtml(base + gif.poster) + '"' : '') +
            ratio + ' muted loop playsinline webkit-playsinline preload="' + (inBubble ? 'metadata' : 'none') + '"' +
            ' disablepictureinpicture disableremoteplayback aria-label="GIF"></video>';
    }

    _playGif(video) {
        // Property zusaetzlich zum Attribut setzen: per innerHTML eingefuegte
        // Videos sind in Safari sonst nicht zuverlaessig stumm -> play() scheitert.
        video.muted = true;
        video.defaultMuted = true;
        video.playsInline = true;
        const p = video.play();
        if (p && typeof p.catch === 'function') {
            // z.B. iOS-Stromsparmodus: kein Autoplay, Vorschaubild bleibt stehen.
            // Tippen startet den Clip (Listener in init()).
            p.catch(() => {});
        }
    }

    // Spielt GIF-Videos in container nur ab, solange sie sichtbar sind.
    // root = Scroll-Element (Picker) oder null (Viewport, Chat). Liefert den Observer.
    _observeGifVideos(container, root) {
        const videos = container ? container.querySelectorAll('video.chat-gif-video') : [];
        if (!videos.length) {
            return null;
        }
        if (!('IntersectionObserver' in window)) {
            videos.forEach(v => this._playGif(v));
            return null;
        }
        const obs = new IntersectionObserver((entries) => {
            entries.forEach(e => {
                if (e.isIntersecting) {
                    this._playGif(e.target);
                } else {
                    e.target.pause();
                }
            });
        }, { root: root || null, threshold: 0.1 });
        videos.forEach(v => obs.observe(v));
        return obs;
    }

    _getRecentGifIds() {
        try {
            const raw = localStorage.getItem('vcs_recent_gifs');
            const arr = raw ? JSON.parse(raw) : [];
            return Array.isArray(arr) ? arr.filter(id => this._gifEntry(id)) : [];
        } catch (_) {
            return [];
        }
    }

    _recordRecentGif(id) {
        try {
            let arr = this._getRecentGifIds().filter(x => x !== id);
            arr.unshift(id);
            arr = arr.slice(0, 12);
            localStorage.setItem('vcs_recent_gifs', JSON.stringify(arr));
        } catch (_) {
            // localStorage nicht verfuegbar -> Recent still ignorieren.
        }
    }

    // Baut ein rohes <emoji-picker> (Locale, i18n, Theme). onPick(unicode) wird
    // bei Emoji-Auswahl aufgerufen. Reiner Emoji-Teil ohne Sticker.
    _buildRawEmojiPicker(onPick) {
        {
            {
                const picker = document.createElement('emoji-picker');
                const lang = VCS.state.currentLang || 'en';
                const supported = ['de', 'en', 'ru'];
                const locale = supported.includes(lang) ? lang : 'en';
                picker.locale = locale;
                picker.dataSource = 'js/emoji-picker-element/data/' + locale + '.json';
                const i18nMap = {
                    de: {
                        categoriesLabel: 'Kategorien',
                        emojiUnsupportedMessage: 'Dein Browser unterstützt keine Farb-Emojis.',
                        favoritesLabel: 'Häufig verwendet',
                        loadingMessage: 'Wird geladen…',
                        networkErrorMessage: 'Emojis konnten nicht geladen werden.',
                        regionLabel: 'Emoji-Auswahl',
                        searchDescription: 'Suchergebnisse mit Pfeiltasten auswählen und mit Enter bestätigen.',
                        searchLabel: 'Suchen',
                        searchResultsLabel: 'Suchergebnisse',
                        skinToneDescription: 'Hautfarbe mit Pfeiltasten auswählen und mit Enter bestätigen.',
                        skinToneLabel: 'Hautfarbe wählen (aktuell {skinTone})',
                        skinTonesLabel: 'Hautfarben',
                        skinTones: ['Standard', 'Hell', 'Mittelhell', 'Mittel', 'Mitteldunkel', 'Dunkel'],
                        categories: {
                            custom: 'Sticker',
                            'smileys-emotion': 'Smileys & Emotionen',
                            'people-body': 'Menschen & Körper',
                            'animals-nature': 'Tiere & Natur',
                            'food-drink': 'Essen & Trinken',
                            'travel-places': 'Reisen & Orte',
                            activities: 'Aktivitäten',
                            objects: 'Objekte',
                            symbols: 'Symbole',
                            flags: 'Flaggen'
                        }
                    },
                    ru: {
                        categoriesLabel: 'Категории',
                        emojiUnsupportedMessage: 'Ваш браузер не поддерживает цветные эмодзи.',
                        favoritesLabel: 'Часто используемые',
                        loadingMessage: 'Загрузка…',
                        networkErrorMessage: 'Не удалось загрузить эмодзи.',
                        regionLabel: 'Выбор эмодзи',
                        searchDescription: 'Используйте стрелки для выбора и Enter для подтверждения.',
                        searchLabel: 'Поиск',
                        searchResultsLabel: 'Результаты поиска',
                        skinToneDescription: 'Используйте стрелки для выбора и Enter для подтверждения.',
                        skinToneLabel: 'Выберите тон кожи (сейчас {skinTone})',
                        skinTonesLabel: 'Тон кожи',
                        skinTones: ['По умолчанию', 'Светлый', 'Средне-светлый', 'Средний', 'Средне-тёмный', 'Тёмный'],
                        categories: {
                            custom: 'Стикеры',
                            'smileys-emotion': 'Смайлы и эмоции',
                            'people-body': 'Люди и тело',
                            'animals-nature': 'Животные и природа',
                            'food-drink': 'Еда и напитки',
                            'travel-places': 'Путешествия и места',
                            activities: 'Занятия',
                            objects: 'Предметы',
                            symbols: 'Символы',
                            flags: 'Флаги'
                        }
                    }
                };
                if (i18nMap[locale]) {
                    picker.i18n = i18nMap[locale];
                }
                const theme = document.documentElement.getAttribute('data-theme');
                if (theme === 'dark') {
                    picker.classList.add('dark');
                } else {
                    picker.classList.add('light');
                }
                picker.addEventListener('emoji-click', (e) => {
                    if (e.detail && e.detail.unicode) {
                        onPick(e.detail.unicode);
                    }
                });
                return picker;
            }
        }
    }

    // Oeffentliche Picker-Factory. Liefert je nach Kontext:
    //  - ohne Sticker (opts.stickers === false, z.B. Bildunterschrift): das rohe
    //    <emoji-picker>.
    //  - mit Sticker: einen Wrapper mit Tab-Leiste [Emoji | Sticker | GIF]. onPick
    //    bekommt ein Unicode-Emoji oder ein Token "sticker:NN" / "gif:N".
    //    opts.gifs === false blendet den GIF-Tab aus (z.B. Reaktionen).
    createEmojiPickerElement(onPick, opts = {}) {
        const rawPicker = this._buildRawEmojiPicker(onPick);
        const withStickers = opts.stickers !== false
            && this._stickerManifest
            && Array.isArray(this._stickerManifest.packs)
            && this._stickerManifest.packs.length > 0;
        const withGifs = opts.stickers !== false && opts.gifs !== false
            && this._gifManifest
            && Array.isArray(this._gifManifest.packs)
            && this._gifManifest.packs.length > 0;
        if (opts.stickers !== false && opts.gifs !== false) {
            // Manifest fehlt oder ist veraltet (Server-Stempel) -> im Hintergrund
            // laden, greift beim naechsten Oeffnen. Aktuell = sofort zurueck.
            this._loadGifManifest();
        }
        if (!withStickers && !withGifs) {
            return rawPicker;
        }

        const wrap = document.createElement('div');
        wrap.className = 'chat-picker';
        const emojiLabel = (VCS.i18n?.t?.('emojiTab')) || 'Emoji';
        const stickerLabel = (VCS.i18n?.t?.('chatSticker')) || 'Sticker';
        const gifLabel = (VCS.i18n?.t?.('chatGif')) || 'GIF';
        wrap.innerHTML =
            '<div class="chat-picker-tabs">' +
                '<button type="button" class="chat-picker-tab chat-picker-tab--active" data-tab="emoji">' + escapeHtml(emojiLabel) + '</button>' +
                (withStickers ? '<button type="button" class="chat-picker-tab" data-tab="sticker">' + escapeHtml(stickerLabel) + '</button>' : '') +
                (withGifs ? '<button type="button" class="chat-picker-tab" data-tab="gif">' + escapeHtml(gifLabel) + '</button>' : '') +
            '</div>' +
            '<div class="chat-picker-body">' +
                '<div class="chat-picker-pane chat-picker-pane--emoji"></div>' +
                (withStickers ? '<div class="chat-picker-pane chat-picker-pane--sticker hidden"></div>' : '') +
                (withGifs ? '<div class="chat-picker-pane chat-picker-pane--gif hidden"></div>' : '') +
            '</div>';
        const emojiPane = wrap.querySelector('.chat-picker-pane--emoji');
        const stickerPane = wrap.querySelector('.chat-picker-pane--sticker');
        const gifPane = wrap.querySelector('.chat-picker-pane--gif');
        emojiPane.appendChild(rawPicker);

        // Sticker-/GIF-Panel erst beim ersten Wechsel bauen (spart Requests).
        let stickerBuilt = false;
        let gifBuilt = false;
        const tabs = wrap.querySelectorAll('.chat-picker-tab');
        tabs.forEach(tab => {
            tab.addEventListener('click', () => {
                const which = tab.getAttribute('data-tab');
                tabs.forEach(t => t.classList.toggle('chat-picker-tab--active', t === tab));
                emojiPane.classList.toggle('hidden', which !== 'emoji');
                stickerPane?.classList.toggle('hidden', which !== 'sticker');
                gifPane?.classList.toggle('hidden', which !== 'gif');
                // Mobil: GIF-Tab bekommt mehr Hoehe (Telegram-Stil), Emoji/Sticker wieder normal.
                wrap.classList.toggle('chat-picker--tall', which === 'gif');
                if (which === 'sticker' && !stickerBuilt) {
                    stickerPane.appendChild(this._buildStickerPanel(onPick));
                    stickerBuilt = true;
                }
                if (which === 'gif' && !gifBuilt) {
                    gifPane.appendChild(this._buildGifPanel(onPick));
                    gifBuilt = true;
                }
            });
        });
        return wrap;
    }

    // Baut das Telegram-artige Sticker-Panel: Scroll-Bereich mit einer Sektion pro
    // Pack (Kopfzeile = Pack-Name) plus Pack-Icon-Leiste unten zum Springen, inkl.
    // "Zuletzt benutzt". Klick auf einen Sticker ruft onPick('sticker:NN') auf.
    _buildStickerPanel(onPick) {
        const panel = document.createElement('div');
        panel.className = 'chat-sticker-panel';
        const packs = (this._stickerManifest && this._stickerManifest.packs) || [];
        const base = this._stickerBase();
        const recentLabel = (VCS.i18n?.t?.('stickerRecent')) || 'Zuletzt benutzt';

        const gridFor = (stickers) => stickers.map(s =>
            '<button type="button" class="chat-sticker-item" data-id="' + escapeHtml(s.id) + '" title="' + escapeHtml(s.name || s.id) + '">' +
            '<img src="' + escapeHtml(base + s.file) + '" alt="' + escapeHtml(s.name || s.id) + '" loading="lazy" draggable="false"></button>'
        ).join('');

        let sectionsHtml = '';
        let navHtml = '';

        // "Zuletzt benutzt"-Sektion nur zeigen, wenn es welche gibt.
        const recentIds = this._getRecentStickerIds();
        if (recentIds.length) {
            const recentStickers = recentIds.map(id => ({ id, file: this._stickerIndex[id].file, name: this._stickerIndex[id].name }));
            sectionsHtml +=
                '<div class="chat-sticker-section" data-section="recent">' +
                    '<div class="chat-sticker-section-title">' + escapeHtml(recentLabel) + '</div>' +
                    '<div class="chat-sticker-grid">' + gridFor(recentStickers) + '</div>' +
                '</div>';
            navHtml +=
                '<button type="button" class="chat-sticker-nav-btn chat-sticker-nav-btn--active" data-target="recent" title="' + escapeHtml(recentLabel) + '">' +
                '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg></button>';
        }

        packs.forEach((p, i) => {
            sectionsHtml +=
                '<div class="chat-sticker-section" data-section="' + escapeHtml(p.id) + '">' +
                    '<div class="chat-sticker-section-title">' + escapeHtml(p.name) + '</div>' +
                    '<div class="chat-sticker-grid">' + gridFor(p.stickers || []) + '</div>' +
                '</div>';
            const iconEntry = this._stickerIndex[p.icon];
            const iconUrl = iconEntry ? (base + iconEntry.file)
                : ((p.stickers && p.stickers[0]) ? (base + p.stickers[0].file) : '');
            const activeCls = (!recentIds.length && i === 0) ? ' chat-sticker-nav-btn--active' : '';
            navHtml +=
                '<button type="button" class="chat-sticker-nav-btn' + activeCls + '" data-target="' + escapeHtml(p.id) + '" title="' + escapeHtml(p.name) + '">' +
                '<img src="' + escapeHtml(iconUrl) + '" alt="' + escapeHtml(p.name) + '" draggable="false"></button>';
        });

        panel.innerHTML =
            '<div class="chat-sticker-scroll">' + sectionsHtml + '</div>' +
            '<div class="chat-sticker-nav">' + navHtml + '</div>';

        // Delegierter Klick: Sticker senden/reagieren.
        panel.addEventListener('click', (e) => {
            const item = e.target.closest ? e.target.closest('.chat-sticker-item') : null;
            if (item) {
                const id = item.getAttribute('data-id');
                if (id) {
                    this._recordRecentSticker(id);
                    onPick('sticker:' + id);
                }
            }
        });
        this._bindPackPanelNav(panel);
        return panel;
    }

    // Baut das GIF-Panel im gleichen Aufbau wie das Sticker-Panel (Sektion pro
    // Pack + Pack-Leiste unten). Klick auf einen Clip ruft onPick('gif:N') auf.
    _buildGifPanel(onPick) {
        const panel = document.createElement('div');
        panel.className = 'chat-sticker-panel chat-gif-panel';
        const packs = (this._gifManifest && this._gifManifest.packs) || [];
        const base = this._gifBase();
        const recentLabel = (VCS.i18n?.t?.('stickerRecent')) || 'Zuletzt benutzt';

        const gridFor = (gifs) => gifs.map(g =>
            '<button type="button" class="chat-gif-item" data-id="' + escapeHtml(String(g.id)) + '" title="' + escapeHtml(g.name || 'GIF') + '">' +
            this._gifVideoHtml(g, false) + '</button>'
        ).join('');

        let sectionsHtml = '';
        let navHtml = '';

        const recentIds = this._getRecentGifIds();
        if (recentIds.length) {
            sectionsHtml +=
                '<div class="chat-sticker-section" data-section="recent">' +
                    '<div class="chat-sticker-section-title">' + escapeHtml(recentLabel) + '</div>' +
                    '<div class="chat-gif-grid">' + gridFor(recentIds.map(id => this._gifEntry(id))) + '</div>' +
                '</div>';
            navHtml +=
                '<button type="button" class="chat-sticker-nav-btn chat-sticker-nav-btn--active" data-target="recent" title="' + escapeHtml(recentLabel) + '">' +
                '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg></button>';
        }

        packs.forEach((p, i) => {
            sectionsHtml +=
                '<div class="chat-sticker-section" data-section="' + escapeHtml(p.id) + '">' +
                    '<div class="chat-sticker-section-title">' + escapeHtml(p.name) + '</div>' +
                    '<div class="chat-gif-grid">' + gridFor(p.gifs || []) + '</div>' +
                '</div>';
            const iconEntry = this._gifEntry(p.icon) || (p.gifs && p.gifs[0]);
            const iconUrl = (iconEntry && iconEntry.poster) ? (base + iconEntry.poster) : '';
            const activeCls = (!recentIds.length && i === 0) ? ' chat-sticker-nav-btn--active' : '';
            navHtml +=
                '<button type="button" class="chat-sticker-nav-btn chat-gif-nav-btn' + activeCls + '" data-target="' + escapeHtml(p.id) + '" title="' + escapeHtml(p.name) + '">' +
                (iconUrl ? '<img src="' + escapeHtml(iconUrl) + '" alt="' + escapeHtml(p.name) + '" draggable="false">' : escapeHtml(p.name.charAt(0))) +
                '</button>';
        });

        panel.innerHTML =
            '<div class="chat-sticker-scroll">' + sectionsHtml + '</div>' +
            '<div class="chat-sticker-nav">' + navHtml + '</div>';

        panel.addEventListener('click', (e) => {
            const item = e.target.closest ? e.target.closest('.chat-gif-item') : null;
            if (item) {
                const id = item.getAttribute('data-id');
                if (id) {
                    this._recordRecentGif(id);
                    onPick('gif:' + id);
                }
            }
        });
        this._bindPackPanelNav(panel);
        // Nur sichtbare Clips im Raster abspielen (Tab versteckt -> alle pausiert).
        this._observeGifVideos(panel, panel.querySelector('.chat-sticker-scroll'));
        return panel;
    }

    // Pack-Leiste eines Sticker-/GIF-Panels: Klick springt zur Sektion, beim
    // Scrollen wird das Icon der obersten Sektion hervorgehoben.
    _bindPackPanelNav(panel) {
        const scroll = panel.querySelector('.chat-sticker-scroll');
        const navBtns = panel.querySelectorAll('.chat-sticker-nav-btn');

        panel.addEventListener('click', (e) => {
            const nav = e.target.closest ? e.target.closest('.chat-sticker-nav-btn') : null;
            if (nav) {
                const target = nav.getAttribute('data-target');
                const safe = (window.CSS && CSS.escape) ? CSS.escape(target) : target;
                const sec = panel.querySelector('.chat-sticker-section[data-section="' + safe + '"]');
                if (sec) {
                    sec.scrollIntoView({ block: 'start', behavior: 'smooth' });
                }
            }
        });

        // Aktives Pack-Icon beim Scrollen hervorheben (naechstliegende Sektion oben).
        scroll.addEventListener('scroll', () => {
            const sections = scroll.querySelectorAll('.chat-sticker-section');
            const top = scroll.scrollTop;
            let current = null;
            sections.forEach(s => {
                if (s.offsetTop <= top + 8) {
                    current = s.getAttribute('data-section');
                }
            });
            if (current) {
                navBtns.forEach(b => b.classList.toggle('chat-sticker-nav-btn--active', b.getAttribute('data-target') === current));
            }
        });
    }

    // Baut den Picker fuer das Chat-Texteingabefeld (#chatTextInput) inkl.
    // Mobil-Tastatur-Handling (iOS Fake-Cursor, Android inputmode).
    _buildEmojiPicker() {
        return this.createEmojiPickerElement((emoji) => {
            // Sticker/GIFs werden nicht ins Textfeld eingefuegt, sondern direkt gesendet.
            const sid = this._stickerIdFromText(emoji);
            if (sid) {
                this.closeEmojiPicker();
                this.sendSticker(sid);
                return;
            }
            const gid = this._gifIdFromText(emoji);
            if (gid) {
                this.closeEmojiPicker();
                this.sendGif(gid);
                return;
            }
            const input = $('#chatTextInput')[0];
            if (!input) {
                return;
            }
            const isIOS = this._isIOS();
            const isMobile = isIOS || /Android/i.test(navigator.userAgent);
            // Auf Mobil kann der Tap auf den Picker dem Feld den Fokus genommen
            // haben -> gemerkte Position nutzen, sonst aktuelle Selektion.
            const start = isMobile
                ? (this._emojiCursorPos ?? input.value.length)
                : input.selectionStart;
            const end = isMobile ? start : input.selectionEnd;
            input.value = input.value.slice(0, start) + emoji + input.value.slice(end);
            const pos = start + emoji.length;
            // Gemerkte Position fortschreiben fuer mehrere Emojis hintereinander
            this._emojiCursorPos = pos;
            if (isIOS) {
                // KEIN focus() -> sonst kommt die iOS-Tastatur zurueck. Nur Selektion
                // setzen (greift erst, wenn der User spaeter wieder ins Feld tippt).
                input.setSelectionRange(pos, pos);
            } else if (isMobile) {
                // Android: Fokus zurueckholen, damit der Cursor weiter im Feld blinkt;
                // inputmode=none haelt die Tastatur dabei unten. Guard nur setzen,
                // wenn der Fokus wirklich wechselt (sonst feuert kein focus-Event
                // und das Flag bliebe haengen).
                if (document.activeElement !== input) {
                    this._emojiKeepFocus = true;
                    input.focus();
                }
                input.setSelectionRange(pos, pos);
            } else {
                // Desktop: Klick in den Picker hat dem Feld den Fokus
                // genommen. focus() holt ihn zurueck -> Guard setzen, damit
                // der focus-Handler den Picker nicht schliesst. Nur wenn der
                // Fokus wirklich wechselt (sonst feuert kein focus-Event und
                // das Flag bliebe haengen).
                if (document.activeElement !== input) {
                    this._emojiKeepFocus = true;
                }
                input.focus();
                input.setSelectionRange(pos, pos);
            }
            // Auto-Resize anstossen (input-Event feuert bei value-Zuweisung nicht)
            input.dispatchEvent(new Event('input'));
            if (isIOS) {
                // Fake-Cursor nach Resize neu platzieren (rueckt mit dem Emoji weiter)
                this._showFakeCaret();
            }
        });
    }

    closeEmojiPicker() {
        const wrap = $('#emojiPickerWrap')[0];
        if (wrap) {
            wrap.classList.add('hidden');
            wrap.classList.remove('emoji-picker-wrap--closing');
            wrap.style.maxHeight = '';
        }
        this._cancelEmojiKeyboardClose();
        document.body.classList.remove('emoji-panel-open');
        this._hideFakeCaret();
        // Tastatur-Unterdrueckung aufheben, damit das naechste echte Tippen
        // die Soft-Tastatur wieder aufruft (Telegram-Stil).
        const input = $('#chatTextInput')[0];
        if (input && input.inputMode === 'none') {
            input.inputMode = 'text';
        }
    }

    // Mobil (Telegram-Stil): Tipp ins Textfeld bei offenem Picker. Der Picker
    // bleibt stehen, bis die Tastatur aufgeht (visualViewport schrumpft), und
    // gleitet erst dann nach unten weg. So springt die Eingabezeile nicht unter
    // dem Finger weg und das Feld behaelt den Fokus. Fallback ohne Tastatur
    // (z.B. Bluetooth-Tastatur): nach 500ms.
    _closeEmojiForKeyboard() {
        const wrap = $('#emojiPickerWrap')[0];
        if (!wrap || wrap.classList.contains('hidden') || this._emojiClosing) {
            return;
        }
        // iOS: sofort schliessen. Safari verkleinert das Layout nicht, sondern
        // scrollt die Seite beim Fokus passend zum DAMALIGEN Layout. Wuerde der
        // Picker erst danach weichen, rutscht die Eingabezeile hinter die Tastatur.
        // (Den Fokus setzt iOS erst nach dem Loslassen -> kein Sprung unterm Finger.)
        if (this._isDesktopEmoji() || this._isIOS()) {
            this.closeEmojiPicker();
            return;
        }
        this._emojiClosing = true;
        this._hideFakeCaret();
        const vv = window.visualViewport;
        const startHeight = vv ? vv.height : 0;
        const slideOut = () => {
            if (!this._emojiClosing) {
                return;
            }
            this._cancelEmojiKeyboardWait();
            wrap.style.maxHeight = wrap.offsetHeight + 'px';
            // Reflow erzwingen, damit die Transition vom Startwert aus laeuft
            void wrap.offsetHeight;
            wrap.classList.add('emoji-picker-wrap--closing');
            wrap.style.maxHeight = '0px';
            this._emojiCloseTimer = setTimeout(() => this.closeEmojiPicker(), 220);
        };
        if (vv) {
            this._emojiVvHandler = () => {
                if (vv.height < startHeight - 100) {
                    slideOut();
                }
            };
            vv.addEventListener('resize', this._emojiVvHandler);
        }
        this._emojiFallbackTimer = setTimeout(slideOut, 500);
    }

    // Wartet nicht mehr auf die Tastatur (Listener + Fallback-Timer weg).
    _cancelEmojiKeyboardWait() {
        clearTimeout(this._emojiFallbackTimer);
        if (this._emojiVvHandler) {
            window.visualViewport?.removeEventListener('resize', this._emojiVvHandler);
            this._emojiVvHandler = null;
        }
    }

    // Bricht ein laufendes Wegschieben komplett ab.
    _cancelEmojiKeyboardClose() {
        this._cancelEmojiKeyboardWait();
        clearTimeout(this._emojiCloseTimer);
        this._emojiClosing = false;
    }

    // iOS: Safari verkleinert das Layout bei offener Tastatur nicht, sondern
    // scrollt die ganze Seite hoch. Dadurch stehen Navigation + Leerraum
    // zwischen Eingabezeile und Tastatur. Loesung: #appShell auf die sichtbare
    // Hoehe (visualViewport) setzen und dem sichtbaren Ausschnitt folgen. Safari
    // verschiebt beim Fokus nur den Ausschnitt (offsetTop), nicht das Fenster ->
    // window.scrollTo greift nicht, daher translateY(offsetTop). Ergebnis: die
    // Eingabezeile sitzt direkt ueber der Tastatur, der Chat-Kopf bleibt oben.
    _startIOSKeyboardFit() {
        const vv = window.visualViewport;
        const shell = $('#appShell')[0];
        if (!vv || !shell || this._iosFitHandler) {
            return;
        }
        this._iosFitHandler = () => {
            const list = $('#chatMessages')[0];
            const atBottom = list
                ? list.scrollHeight - list.scrollTop - list.clientHeight < 80
                : false;
            // .h-100 setzt height mit !important -> inline ebenfalls important
            shell.style.setProperty('height', vv.height + 'px', 'important');
            shell.style.transform = 'translateY(' + vv.offsetTop + 'px)';
            // Wer unten im Chat war, bleibt unten (letzte Nachricht sichtbar)
            if (list && atBottom) {
                list.scrollTop = list.scrollHeight;
            }
        };
        vv.addEventListener('resize', this._iosFitHandler);
        vv.addEventListener('scroll', this._iosFitHandler);
        this._iosFitHandler();
    }

    _stopIOSKeyboardFit() {
        if (this._iosFitHandler) {
            window.visualViewport?.removeEventListener('resize', this._iosFitHandler);
            window.visualViewport?.removeEventListener('scroll', this._iosFitHandler);
            this._iosFitHandler = null;
        }
        const shell = $('#appShell')[0];
        if (shell) {
            shell.style.removeProperty('height');
            shell.style.transform = '';
        }
    }

    // iOS inkl. iPadOS (iPad meldet sich teils als MacIntel mit Touch)
    _isIOS() {
        const ua = navigator.userAgent;
        if (/iPhone|iPad|iPod/i.test(ua)) {
            return true;
        }
        return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
    }

    // Fake-Cursor (iOS): nativer Cursor ist ohne Fokus nicht moeglich. Bei offenem
    // Emoji-Picker zeichnen wir einen blinkenden Balken an die Cursor-Position.
    _showFakeCaret() {
        const input = $('#chatTextInput')[0];
        const row = input?.closest('.chat-input-row');
        if (!input || !row) {
            return;
        }
        const pos = this._emojiCursorPos ?? input.value.length;
        const coords = this._caretCoords(input, pos);
        if (!coords) {
            return;
        }
        let caret = this._fakeCaretEl;
        if (!caret) {
            caret = document.createElement('div');
            caret.className = 'emoji-fake-caret';
            row.appendChild(caret);
            this._fakeCaretEl = caret;
        }
        // Versatz der Textarea innerhalb der Input-Row (Anker ist position:relative).
        // CSS left/top beziehen sich auf die Padding-Box des Ankers, getBoundingClientRect
        // liefert die Border-Box -> Anker-Border herausrechnen.
        const inputRect = input.getBoundingClientRect();
        const rowRect = row.getBoundingClientRect();
        const rowStyle = getComputedStyle(row);
        const rowBorderL = parseFloat(rowStyle.borderLeftWidth) || 0;
        const rowBorderT = parseFloat(rowStyle.borderTopWidth) || 0;
        const offX = inputRect.left - rowRect.left - rowBorderL;
        const offY = inputRect.top - rowRect.top - rowBorderT;
        caret.style.left = (offX + coords.left - input.scrollLeft) + 'px';
        caret.style.top = (offY + coords.top - input.scrollTop) + 'px';
        caret.style.height = coords.height + 'px';
        // Blink-Animation bei jeder Neuplatzierung von vorne starten
        caret.style.animation = 'none';
        // Reflow erzwingen, damit das Neusetzen der Animation greift
        void caret.offsetHeight;
        caret.style.animation = '';
    }

    _hideFakeCaret() {
        if (this._fakeCaretEl) {
            this._fakeCaretEl.remove();
            this._fakeCaretEl = null;
        }
    }

    // Pixel-Koordinaten des Cursors in einer Textarea via Mirror-Element ermitteln.
    // Ein unsichtbares div spiegelt alle layout-relevanten Styles, der Text bis zur
    // Cursor-Position wird eingefuegt und ein Marker-Span vermessen.
    _caretCoords(textarea, position) {
        const style = getComputedStyle(textarea);
        const div = document.createElement('div');
        div.style.position = 'absolute';
        div.style.top = '-9999px';
        div.style.left = '-9999px';
        div.style.visibility = 'hidden';
        div.style.whiteSpace = 'pre-wrap';
        div.style.wordWrap = 'break-word';
        div.style.boxSizing = 'content-box';
        // Inhaltsbreite exakt wie die Textarea, damit Zeilenumbrueche identisch fallen
        const padL = parseFloat(style.paddingLeft) || 0;
        const padR = parseFloat(style.paddingRight) || 0;
        div.style.width = (textarea.clientWidth - padL - padR) + 'px';
        const copyProps = [
            'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
            'borderTopWidth', 'borderRightWidth', 'borderBottomWidth', 'borderLeftWidth',
            'fontStyle', 'fontVariant', 'fontWeight', 'fontStretch', 'fontSize',
            'lineHeight', 'fontFamily', 'textAlign', 'textTransform', 'textIndent',
            'letterSpacing', 'wordSpacing', 'tabSize', 'direction'
        ];
        copyProps.forEach((p) => {
            div.style[p] = style[p];
        });
        div.textContent = textarea.value.substring(0, position);
        const span = document.createElement('span');
        // Rest-Text bzw. Platzhalter, damit der Span eine messbare Box hat
        span.textContent = textarea.value.substring(position) || '.';
        div.appendChild(span);
        document.body.appendChild(div);
        const coords = {
            left: span.offsetLeft + (parseFloat(style.borderLeftWidth) || 0),
            top: span.offsetTop + (parseFloat(style.borderTopWidth) || 0),
            height: parseFloat(style.lineHeight) || parseFloat(style.fontSize) || 16
        };
        document.body.removeChild(div);
        return coords;
    }

    resetEmojiPicker() {
        const wrap = $('#emojiPickerWrap')[0];
        if (wrap) {
            wrap.innerHTML = '';
            wrap.classList.add('hidden');
        }
        document.body.classList.remove('emoji-panel-open');
        this._hideFakeCaret();
        this._emojiPickerReady = false;
        clearTimeout(this._emojiHoverTimer);
        this.closeEmojiSidebar();
    }

    startRecording() {
        if (VCS.state.chatRecording) {
            return;
        }
        if (VCS.state.chatRecordMode === 'video') {
            VCS.chatVideo?.startRecording?.();
            return;
        }
        VCS.chatVoice?.startRecording?.();
    }

    stopRecording() {
        VCS.chatVoice?.stopRecording?.();
    }

    cancelRecording() {
        VCS.chatVoice?.cancelRecording?.();
    }

    // ── Init ─────────────────────────────────────────────────

    init() {
        // Sticker-Manifest vorab laden; nach dem Laden einmal neu rendern, damit
        // bereits sichtbare Sticker-Nachrichten ihre Bilder bekommen.
        this._loadStickerManifest().then((m) => {
            if (m && VCS.state.chatActiveChatId) {
                this.renderChatMessages();
            }
            if (m) {
                this.renderChatList();
            }
        });
        // GIF-Manifest ebenso; danach bekommen sichtbare GIF-Nachrichten ihr Video
        // und die Chat-Liste den GIF-Namen in der Vorschau.
        this._loadGifManifest().then((m) => {
            if (m && VCS.state.chatActiveChatId) {
                this.renderChatMessages();
            }
            if (m) {
                this.renderChatList();
            }
        });
        // GIF im Chat angetippt und steht still (z.B. iOS-Stromsparmodus blockt
        // Autoplay): der Tipp ist eine Nutzer-Geste, damit darf es starten.
        $('#chatMessages')[0]?.addEventListener('click', (e) => {
            const v = e.target.closest ? e.target.closest('video.chat-gif-video') : null;
            if (v && v.paused) {
                this._playGif(v);
            }
        });

        // Emoji Picker
        $('#chatEmojiBtn')[0]?.addEventListener('click', (e) => {
            e.stopPropagation();
            this.toggleEmojiPicker();
        });

        // Tipp auf Emoji/Anhang/Senden darf dem Textfeld NICHT vorab den Fokus
        // nehmen: der blur-Listener blendet sonst die Navigation ein (iOS: App-
        // Hoehe zurueck) -> die Zeile springt und der click landet daneben.
        ['#chatEmojiBtn', '#chatAttachBtn', '#chatSendBtn'].forEach((sel) => {
            $(sel)[0]?.addEventListener('mousedown', (e) => e.preventDefault());
        });

        // Desktop (Telegram-Stil): Hover ueber den Button zeigt das schwebende
        // Popup, solange die Seitenleiste nicht offen ist. Der Maus-Wechsel
        // Button<->Popup wird durch einen kurzen Timer ueberbrueckt.
        const emojiBtn = $('#chatEmojiBtn')[0];
        emojiBtn?.addEventListener('mouseenter', () => {
            if (!this._isDesktopEmoji()) {
                return;
            }
            clearTimeout(this._emojiHoverTimer);
            this._openEmojiHoverPopup();
        });
        emojiBtn?.addEventListener('mouseleave', () => {
            if (!this._isDesktopEmoji()) {
                return;
            }
            this._scheduleEmojiHoverClose();
        });
        const emojiWrap = $('#emojiPickerWrap')[0];
        emojiWrap?.addEventListener('mouseenter', () => {
            clearTimeout(this._emojiHoverTimer);
        });
        emojiWrap?.addEventListener('mouseleave', () => {
            if (!this._isDesktopEmoji()) {
                return;
            }
            this._scheduleEmojiHoverClose();
        });

        // Programmatischer Fokus (Picker offen, Cursor blinkt) soll den Picker NICHT
        // schliessen. Nur ein echter User-Fokus blendet ihn aus.
        $('#chatTextInput')[0]?.addEventListener('focus', () => {
            if (this._emojiKeepFocus) {
                this._emojiKeepFocus = false;
                return;
            }
            this._closeEmojiForKeyboard();
        });

        // Mobil (Telegram-Stil): solange das Textfeld den Fokus hat, die untere
        // Navigation ausblenden -> Eingabezeile direkt ueber der Tastatur.
        // iOS zusaetzlich App-Hoehe an den sichtbaren Bereich koppeln.
        $('#chatTextInput')[0]?.addEventListener('focus', () => {
            document.body.classList.add('chat-input-focused');
            if (this._isIOS() && !this._isDesktopEmoji()) {
                this._startIOSKeyboardFit();
            }
        });
        $('#chatTextInput')[0]?.addEventListener('blur', () => {
            document.body.classList.remove('chat-input-focused');
            this._stopIOSKeyboardFit();
        });

        // Android-Fall: das Feld ist bei offenem Picker bereits fokussiert (inputmode=none),
        // ein Tipp loest also KEIN focus-Event aus -> hier die Tastatur zurueckholen.
        // Auf iOS ist das Feld geblurrt, dort regelt der natuerliche focus den Rest.
        $('#chatTextInput')[0]?.addEventListener('pointerdown', () => {
            const wrap = $('#emojiPickerWrap')[0];
            if (!wrap || wrap.classList.contains('hidden')) {
                return;
            }
            const input = $('#chatTextInput')[0];
            if (!input || document.activeElement !== input) {
                return;
            }
            // Picker NICHT sofort schliessen: das wuerde die Eingabezeile unter dem
            // Finger verschieben und der Fokus landete beim Loslassen daneben.
            // Hier nur inputmode zuruecksetzen; blur+focus erst im click-Handler,
            // denn bei Touch zaehlt erst das Loslassen als Nutzer-Geste (sonst
            // oeffnet Chrome die Tastatur nicht).
            input.inputMode = 'text';
        });

        // Android: Tipp ins Feld bei offenem Picker -> Tastatur holen (click laeuft
        // nach dem Loslassen, also mit Nutzer-Geste), Cursor-Position behalten und
        // den Picker sanft weichen lassen. iOS: Feld war geblurrt, dort hat der
        // focus-Listener das Weichen schon gestartet (_emojiClosing) -> nichts tun.
        $('#chatTextInput')[0]?.addEventListener('click', () => {
            const wrap = $('#emojiPickerWrap')[0];
            if (!wrap || wrap.classList.contains('hidden') || this._emojiClosing) {
                return;
            }
            const input = $('#chatTextInput')[0];
            if (!input) {
                return;
            }
            const start = input.selectionStart;
            const end = input.selectionEnd;
            input.inputMode = 'text';
            this._emojiKeepFocus = true;
            input.blur();
            input.focus();
            this._emojiKeepFocus = false;
            input.setSelectionRange(start, end);
            this._closeEmojiForKeyboard();
        });

        document.addEventListener('click', (e) => {
            const wrap = $('#emojiPickerWrap')[0];
            if (!wrap || wrap.classList.contains('hidden')) {
                return;
            }
            if (wrap.contains(e.target) || e.target.closest('#chatEmojiBtn')) {
                return;
            }
            // Tipp ins Textfeld regeln pointerdown/focus (Picker weicht der Tastatur).
            if (e.target.closest('#chatTextInput')) {
                return;
            }
            this.closeEmojiPicker();
        });

        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
                this.closeEmojiPicker();
                this.closeEmojiSidebar();
            }
        });

        // Text senden
        $('#chatSendBtn')[0]?.addEventListener('click', () => this.sendText());
        $('#chatTextInput')[0]?.addEventListener('keydown', (e) => {
            if (e.key !== 'Enter') return;
            const sendKey = localStorage.getItem('chat-send-key') || 'newline';
            if (sendKey === 'enter') {
                if (e.ctrlKey || e.metaKey) {
                    // Strg+Enter = neue Zeile manuell einfuegen
                    e.preventDefault();
                    const ta = e.target;
                    const start = ta.selectionStart;
                    ta.value = ta.value.substring(0, start) + '\n' + ta.value.substring(ta.selectionEnd);
                    ta.selectionStart = ta.selectionEnd = start + 1;
                    ta.dispatchEvent(new Event('input'));
                } else if (!e.shiftKey) {
                    // Enter = Senden
                    e.preventDefault();
                    this.sendText();
                }
            } else {
                // Enter = neue Zeile, Ctrl/Cmd+Enter = Senden
                if (e.ctrlKey || e.metaKey) {
                    e.preventDefault();
                    this.sendText();
                }
            }
        });

        // Textarea Auto-Resize + Typing
        const autoResizeInput = () => {
            const el = $('#chatTextInput')[0];
            if (!el) {
                return;
            }
            el.style.height = 'auto';
            el.style.height = el.scrollHeight + 'px';
            el.style.overflowY = el.scrollHeight > 120 ? 'auto' : 'hidden';
        };

        $('#chatTextInput')[0]?.addEventListener('input', () => {
            autoResizeInput();
            this.saveDraft();
            if (VCS.state.chatActiveChatId) {
                this.sendTyping();
            }
        });

        // Send/Mic Toggle
        $('#chatTextInput')[0]?.addEventListener('input', () => {
            const hasText = $('#chatTextInput')[0].value.trim().length > 0;
            $('#chatSendBtn')[0]?.classList.toggle('hidden', !hasText);
            $('#chatMicBtn')[0]?.classList.toggle('hidden', hasText);
        });

        // Recording abbrechen
        $('#chatRecordingCancel')[0]?.addEventListener('click', () => this.cancelRecording());

        // Reply-Bar schließen
        $('#chatReplyClose')[0]?.addEventListener('click', () => this.clearReply());

        // Scroll-FAB + aeltere Nachrichten nachladen (Hochscrollen)
        $('#chatMessages')[0]?.addEventListener('scroll', () => { this.updateScrollFab(); this.maybeLoadOlder(); });
        $('#chatScrollFab')[0]?.addEventListener('click', () => this.scrollFabClick());

        // Reaction-Bar: Emoji-Buttons reagieren direkt, der Mehr-Button oeffnet
        // den schwebenden Emoji-Picker fuer beliebige Reactions.
        $('#chatReactionBar')[0]?.querySelectorAll('.chat-reaction-btn').forEach(btn => {
            if (btn.dataset.action === 'more-reactions') {
                btn.addEventListener('click', (e) => {
                    e.stopPropagation();
                    this.openReactionEmojiPicker();
                });
            } else {
                btn.addEventListener('click', () => this.toggleReaction(btn.dataset.emoji));
            }
        });

        // Context-Menu Aktion-Handler
        $('#chatContextMenu')[0]?.querySelectorAll('button[data-action]').forEach(btn => {
            btn.addEventListener('click', () => this.handleContextAction(btn.dataset.action));
        });

        // Context-Menu Overlay schließen
        $('#chatContextOverlay')[0]?.addEventListener('click', () => this.hideContextMenu());

        // Context-Menu schließen bei Klick außerhalb. Der schwebende Reaction-
        // Emoji-Picker liegt ausserhalb des Wraps und zaehlt hier als "innen".
        document.addEventListener('click', (e) => {
            const wrap = $('#chatContextMenuWrap')[0];
            const inReactionPicker = e.target.closest('#reactionEmojiWrap');
            if (wrap && !wrap.contains(e.target) && !inReactionPicker && !wrap.classList.contains('hidden')) {
                this.hideContextMenu();
            }
        });

        // Klick auf Read-Status (Gruppen) → Leser-Popup
        document.addEventListener('click', (e) => {
            const statusEl = e.target.closest('[data-readers-msg-id]');
            if (statusEl) {
                e.stopPropagation();
                this.showReadersPopup(statusEl.dataset.readersMsgId, statusEl);
            }
        });

        // Chat-Detail Zurück
        $('#chatDetailBack')[0]?.addEventListener('click', () => {
            VCS.state.chatActiveChatId = null;
            VCS.signaling?.sendActiveChat?.();
            this.clearReply();
            VCS.state.chatEditingMessageId = null;
            this.hideContextMenu();
            this.closeEmojiPicker();
            // Fokus raus -> blur-Listener blendet Navigation wieder ein (iOS: App-Hoehe zurueck)
            $('#chatTextInput')[0]?.blur();
            $('#chatTypingIndicator')[0]?.classList.add('hidden');
            $('#chatRecordingIndicator')[0]?.classList.add('hidden');

            if (VCS.state.chatCurrentlyPlaying) {
                const prev = VCS.state.chatAudioPlayers[VCS.state.chatCurrentlyPlaying.messageId];
                if (prev && prev[VCS.state.chatCurrentlyPlaying.type]) {
                    prev[VCS.state.chatCurrentlyPlaying.type].pause();
                }
                VCS.state.chatCurrentlyPlaying = null;
            }

            if (VCS.nav?.isDesktopLayout()) {
                document.querySelectorAll('.chat-list-item').forEach(el => {
                    el.classList.remove('chat-list-item--active');
                });
                desktopShowEmptyState();
            } else {
                $('#chatDetailScreen')[0]?.classList.add('hidden');
                VCS.nav?.switchTab?.('tabChats');
            }
        });

        // Übersetzungs-Toggle. Nativer <button role="switch"> statt
        // label+hidden-checkbox, weil letzteres auf iOS Safari / Samsung
        // Internet bei kleinen Touch-Targets oft keine Events feuert.
        $('#chatTranslateToggle')[0]?.addEventListener('click', () => {
            if (!VCS.state.chatActiveChatId) return;
            const btn = $('#chatTranslateToggle')[0];
            if (btn.hasAttribute('disabled')) return;
            VCS.e2e?.toggleTranslate?.(VCS.state.chatActiveChatId);
        });

        // Call-Buttons im Chat-Header
        $('#chatCallAudioBtn')[0]?.addEventListener('click', async () => {
            if (!VCS.state.chatActiveChatId) {
                return;
            }
            const chat = VCS.state.chatChats.find(c => c.id === VCS.state.chatActiveChatId);
            if (!chat) {
                return;
            }
            const phone = VCS.chat?.peerPhone?.(chat);
            const name = $('#chatDetailPeerName')[0]?.textContent || phone;
            const iconSvg = '<svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>';
            const ok = await VCS.ui?.showConfirm?.(t('confirmStartAudioCall').replace('{0}', name), iconSvg);
            if (ok) {
                VCS.chatContacts?.callContact?.(phone, name, 'audio');
            }
        });

        $('#chatCallVideoBtn')[0]?.addEventListener('click', async () => {
            if (!VCS.state.chatActiveChatId) {
                return;
            }
            const chat = VCS.state.chatChats.find(c => c.id === VCS.state.chatActiveChatId);
            if (!chat) {
                return;
            }
            const phone = VCS.chat?.peerPhone?.(chat);
            const name = $('#chatDetailPeerName')[0]?.textContent || phone;
            const iconSvg = '<svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>';
            const ok = await VCS.ui?.showConfirm?.(t('confirmStartVideoCall').replace('{0}', name), iconSvg);
            if (ok) {
                VCS.chatContacts?.callContact?.(phone, name, 'video');
            }
        });

        // Minuten teilen (2026-08-31): Ein-/Ausschalten je Kontakt. Sichtbarkeit
        // des Buttons regelt chat-setup.js openConversation (kein Bot/Gruppe/
        // Dating/Free), die eigentliche Logik sitzt bei chat-contacts.js wie bei
        // den anderen Kontakt-Aktionen (callContact).
        $('#chatShareMinutesBtn')[0]?.addEventListener('click', async () => {
            if (!VCS.state.chatActiveChatId) return;
            const chat = VCS.state.chatChats.find(c => c.id === VCS.state.chatActiveChatId);
            if (!chat) return;
            const phone = VCS.chat?.peerPhone?.(chat);
            if (!phone) return;
            const name = $('#chatDetailPeerName')[0]?.textContent || phone;
            VCS.chatContacts?.toggleShareMinutes?.(phone, name);
        });

        // Bottom Navigation
        document.querySelectorAll('#bottomNav .bottom-nav-item').forEach(btn => {
            btn.addEventListener('click', () => {
                const tabId = btn.dataset.tab;
                if (tabId) {
                    VCS.nav?.switchTab?.(tabId);
                }
            });
        });

        // Neuer Chat / Neuer Kontakt
        // Der "+"-Button oeffnet jetzt zuerst ein Auswahl-Popover (Neuer Chat /
        // Live-Unterhaltung). "Neuer Chat" ruft im Popover exakt den heutigen
        // Modal-Pfad. Fallback (Popover-Modul fehlt): direkt Modal oeffnen.
        $('#btnNewChat')[0]?.addEventListener('click', (e) => {
            if (VCS.liveConvo?.togglePlusMenu) {
                VCS.liveConvo.togglePlusMenu(e.currentTarget);
                return;
            }
            const modalEl = $('#newChatModal')[0];
            if (modalEl && window.bootstrap?.Modal) {
                const title = modalEl.querySelector('.modal-title');
                if (title) {
                    title.textContent = t('chatNew');
                }
                bootstrap.Modal.getOrCreateInstance(modalEl).show();
            }
        });

        $('#btnNewContact')[0]?.addEventListener('click', () => {
            const modalEl = $('#newChatModal')[0];
            if (modalEl && window.bootstrap?.Modal) {
                const title = modalEl.querySelector('.modal-title');
                if (title) {
                    title.textContent = t('contactAdd');
                }
                bootstrap.Modal.getOrCreateInstance(modalEl).show();
            }
        });

        $('#btnStartChat')[0]?.addEventListener('click', () => VCS.chatContacts?.startNewChat?.());
        $('#btnCreateInvite')[0]?.addEventListener('click', () => VCS.chatContacts?.createInviteLink?.());
        $('#newChatPhoneInput')[0]?.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                VCS.chatContacts?.startNewChat?.();
            }
        });

        // Settings: Sprachauswahl
        $('#settingsLangSelect')[0]?.addEventListener('change', (e) => {
            VCS.i18n?.setLang(e.target.value);
        });

        // Theme toggle
        document.querySelectorAll('.theme-toggle-btn').forEach(function (btn) {
            btn.addEventListener('click', function () {
                VCS.ui?.setThemeMode(btn.dataset.theme);
            });
        });

        // Settings: Benachrichtigungen
        const notifyToggle = $('#settingsNotifyToggle')[0];
        if (notifyToggle) {
            notifyToggle.checked = VCS.state.notifyEnabled && Notification.permission === 'granted';
            notifyToggle.addEventListener('change', async () => {
                await VCS.notify?.handleNotifyToggle();
                notifyToggle.checked = VCS.state.notifyEnabled && Notification.permission === 'granted';
            });
        }

        // Settings: Ruhemodus (nur dieses Geraet / diese App)
        const quietToggle = $('#settingsQuietToggle')[0];
        if (quietToggle) {
            quietToggle.checked = !!VCS.state.quietMode;
            quietToggle.addEventListener('change', () => {
                VCS.notify?.setQuietMode?.(quietToggle.checked);
            });
        }

        // Profil: Name ändern
        $('#profileNameInput')[0]?.addEventListener('input', () => {
            const btn = $('#btnProfileSaveName')[0];
            const current = ($('#profileNameInput')[0]?.value || '').trim();
            if (btn) {
                btn.classList.toggle('hidden', current === VCS.state.chatMyName || !current);
            }
        });
        $('#btnProfileSaveName')[0]?.addEventListener('click', () => VCS.chatSetup?.saveName());

        // Cache löschen
        this._updateCacheSize();
        $('#btnClearCache')[0]?.addEventListener('click', async () => {
            const t = VCS.i18n?.t?.bind(VCS.i18n) || (k => k);
            if (!await VCS.ui?.showConfirm?.(t('clearCacheConfirm'), '', t('clearCacheBtn'))) return;
            await VCS.mediaCache?.clear?.();
            if ('caches' in window) {
                try {
                    const names = await caches.keys();
                    await Promise.all(names.map(n => caches.delete(n)));
                } catch (_) {}
            }
            const el = $('#cacheSizeInfo')[0];
            if (el) el.textContent = t('cacheSize').replace('{size}', '0.0').replace('{count}', '0');
            VCS.ui?.showToast?.(t('clearCacheDone'));
        });

        // Abmelden
        $('#btnUnregister')[0]?.addEventListener('click', () => VCS.chatSetup?.unregister?.());

        // Sicherheitspasswort
        $('#settingsPasswordInput')[0]?.addEventListener('input', () => {
            const btn = $('#btnSavePassword')[0];
            const val = ($('#settingsPasswordInput')[0]?.value || '').trim();
            if (btn) {
                btn.classList.toggle('hidden', !val);
            }
        });
        $('#btnSavePassword')[0]?.addEventListener('click', async () => {
            await VCS.e2e?.savePassword?.();
            $('#btnSavePassword')[0]?.classList.add('hidden');
        });

        // Sprachauswahl auf Registrierungs-Screen (Card A) und Login-Screen (Card B).
        // Beim Wechsel synchronisieren wir beide Selects, damit der zuletzt gewaehlte
        // Wert auf beiden Karten konsistent angezeigt wird.
        const langSelectIds = ['#regLangSelect', '#loginLangSelect'];
        const langSelectEls = langSelectIds.map((id) => $(id)[0]).filter(Boolean);
        for (const sel of langSelectEls) {
            for (const code of AVAILABLE_LANGS) {
                const opt = document.createElement('option');
                opt.value = code;
                opt.textContent = LANG_NAMES[code] || code.toUpperCase();
                sel.appendChild(opt);
            }
            sel.value = VCS.state.currentLang;
            sel.addEventListener('change', (e) => {
                const lang = e.target.value;
                VCS.i18n?.setLang(lang);
                for (const other of langSelectEls) {
                    if (other !== e.target) other.value = lang;
                }
            });
        }

        // Initial UI
        VCS.chatSetup?.updateRegistrationUI?.();
        VCS.chatRecordingUI?.updateRecordButtonUI?.();
        VCS.chatRecordingUI?.updateRecordingBarMode?.();
    }

    // ── Private Methoden ─────────────────────────────────────

    async _updateCacheSize() {
        const el = $('#cacheSizeInfo')[0];
        if (!el) return;
        try {
            const stats = await VCS.mediaCache?.getStats?.();
            if (!stats) { el.textContent = ''; return; }
            const mb = (stats.totalSize / (1024 * 1024)).toFixed(1);
            const t = VCS.i18n?.t?.bind(VCS.i18n) || (k => k);
            el.textContent = t('cacheSize').replace('{size}', mb).replace('{count}', stats.entryCount);
        } catch (_) {
            el.textContent = '';
        }
    }

    _updateChatUnreadBadge() {
        const badge = $('#navChatBadge')[0];
        if (!badge) {
            return;
        }
        const total = Object.values(VCS.state.chatUnreadCounts).reduce((sum, n) => sum + n, 0);
        if (total > 0) {
            badge.textContent = total;
            badge.classList.remove('hidden');
        } else {
            badge.classList.add('hidden');
        }
    }
};
